import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Database, ChevronDown, ChevronRight, ChevronLeft, ChevronUp, Table, Home, Plus, Settings, ArrowUp, GripVertical, Sparkles, Zap, Paperclip, Bot, X, Search, Pencil, Filter, Check, Loader2, Network, Copy, Trash2, Eye, BookOpen, Link2, Square, Shield } from 'lucide-react';
import SemanticCanvas from '../semanticCanvasV2';
import { SearchDisplayMode, SearchType } from '../semanticCanvasV2/types/TCanvasOptions';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FloatingProgressPill from '../components/progress-styles/TerminalProgress';
import DelayedReveal from '../components/DelayedReveal';
import DataProductGraphView from '../components/DataProductGraphView';
import StreamingSQL from '../components/StreamingSQL';
import DataProductTable from '../components/DataProductTable';
import DataExplorationChart from '../components/DataExplorationChart';
import StepCard from '../components/StepCard';
import QueryResponseUI from '../components/query/QueryResponseUI';
import PublishPanel from '../components/query/PublishPanel';
import { PublishInputForm, PublishProgress } from '../components/query/PublishFlow';

// Generate semantic graph data dynamically based on chat flow state

// Mock data structure from Excel (for auto-classification) - Moved to module scope for reuse
const autoDataStructure = {
  "Marketing": {
    "Campaign Survey": ["Campaign Type", "Campaign Name", "Survey Date", "Participant Name", "Participant Score", "Feedback / Comments", "Campaign ID", "State", "Country", "Camp ID"],
    "Campaigns": ["Engagement ID", "Prospect ID", "Prod_ID", "Campaign ID", "Campaign Type", "Campaign Date", "Campaign Name", "Participant Name", "Participant In Time", "Participant Out Time", "Customer / Prospect Flag", "Prospect Name", "Company Name", "Cost / Expense ($)", "Eng ID", "Prospect_ID"],
    "Website": ["Customer Session ID", "Contact Name", "Website Section", "Content_Click", "Demo Request", "Enquiry Submission Flag", "Session Login Time", "Session Log Out Time", "Campaign ID", "Cmgn ID"]
  },
  "Customer": {
    "Customer Demographics": ["Customer Name", "Address", "Industry / Vertical", "Annual Revenue", "C_ID", "Region", "Customer Type", "Employee Count", "Contact Number"],
    "Customer Hierarchy": ["Party Name", "Global / Local Entity", "Global / Parent Account", "Party ID", "Subsidiary Name", "Relationship Type", "Region", "Account Manager"]
  },
  "Supply Chain": {
    "Delivery_Survey": ["Customer Feedback", "Survey Score", "Order ID", "Customer ID", "Delivery Date", "Delivery Partner", "Product ID", "Resolution Status", "Odr ID"],
    "Inventory": ["Inventory ID", "Product Name", "Inventory (Units)", "Quantity", "Inventory Location", "Inventory Status", "Prod_ID", "Last Updated Date", "Supplier Name", "Reorder Threshold", "Storage Condition"],
    "Logistics": ["Logistics ID", "Product Name", "Order Date", "Order Status", "Fulfilment Date", "Delivery Date", "Delivery Status", "Consignment Location", "Cust_ID", "Contract_ID", "Od_Contract_ID"]
  },
  "Sales & Orders": {
    "Expense": ["Expense ID", "Expense Type", "Expense Date", "Cost / Expense ($)", "Contact ID", "Contact Name", "Customer Name", "Department", "Payment Method", "Invoice Number", "Approval Status"],
    "Install Base": ["Order ID", "Customer Name", "Order Date", "Order Status", "Order Value ($)", "Order Qty", "Product Name", "Product Desc", "Product Family", "Ship to Country", "Bill to Country", "Party ID", "Product ID", "Odr_ID"],
    "Opportunity": ["Opportunity ID", "Sales Rep ID", "Sales Rep Name", "Region", "Country", "Cnt ID", "Contact Name", "Customer Name", "Opp Initiation Date", "Opp Close Date", "Opportunity Status", "Opportunity Stage", "Cust ID"],
    "Renewals": ["Customer Name", "Product Name", "Cus ID", "Order ID", "Product ID", "Value ($)", "Quantity", "Od ID"]
  },
  "Support & Services": {
    "NPS Survey": ["NPS_ID", "Customer Feedback", "Survey Score", "Return_ID", "Customer_ID", "Survey Date", "Channel", "Region", "Surveyor / Agent"],
    "Prob_Statement_Issue": ["Problem Statement", "Description", "Issue ID", "Summary", "Customer Name", "Product Name", "SR ID", "Product_ID"],
    "Returns": ["Return Type", "Return Creation Date", "Return Closed Date", "Customer ID", "Product ID", "Order ID", "Return Status", "Return Value ($)", "Cost ($)", "R Order ID"],
    "Service Request": ["SR ID", "SR Type", "SR Creation Date", "SR Closed Date", "Prod ID", "C ID", "SR Status", "SR Engineer", "Outage", "Srv_ID", "R_Order ID", "Sales Ord ID"]
  },
  "Products": {
    "Product Engg": ["Product Name", "Product Category", "Product Status", "R&D Initiation Date", "R&D Stage", "Launch Status", "Launch Date", "PM Name", "Product Cost ($)", "Product ID"],
    "Product Feature": ["Prod Name", "Weight (Oz)", "Length (cm)", "Width (Cm)", "Height (Cm)", "Capacity", "Temperature (F)", "P_ID"],
    "Product Hierarchy": ["Product Name", "Product Desc Short", "Product Desc Long", "Product Family", "SKU ID", "Technology Group", "Product ID"]
  }
};

const generateSemanticGraphData = (step, selectedTablesData = [], domainClassification = null, isAutoDomain = false, classifiedTableCount = null) => {
  const nodes = [];
  const links = [];
  
  // STEP 1: Connection established - return empty graph (only background)
  if (step === 'context' || step === 'table-selection') {
    return { nodes: [], links: [] };
  }
  
  // Distinctive color palette for domains (12 VERY BRIGHT colors optimized for dark blue background)
  const DOMAIN_COLOR_PALETTE = [
    "#FF4D4D", // Vivid Red
    "#FF6B4D", // Bright Coral
    "#FF8844", // Bright Orange  
    "#FFB84D", // Bright Gold
    "#FFE74D", // Bright Yellow
    "#B4FF4D", // Bright Lime
    "#4DFFB4", // Bright Emerald
    "#4DFFFF", // Bright Cyan
    "#4DB4FF", // Bright Blue
    "#8B88FF", // Bright Indigo
    "#C688FF", // Bright Violet
    "#FF88D4", // Bright Pink
  ];
  
  // Predefined colors for common domain names (using brighter colors with RED included)
  const domainColors = {
    "Marketing": "#FF4D4D",      // Vivid Red - NOW USING RED!
    "Customer": "#4DFFFF",       // Bright Cyan
    "Supply Chain": "#4DFFB4",   // Bright Emerald
    "Sales & Orders": "#FFB84D", // Bright Gold
    "Support & Services": "#C688FF", // Bright Violet
    "Products": "#4DB4FF"        // Bright Blue
  };
  
  // Generate color from palette with index to avoid duplicates
  const getDomainColor = (domainName, index) => {
    // Check if predefined color exists
    if (domainColors[domainName]) {
      return domainColors[domainName];
    }
    // Use palette index, cycling through if more than 12 domains
    return DOMAIN_COLOR_PALETTE[index % DOMAIN_COLOR_PALETTE.length];
  };
  
  // STEP 2: After table selection - show tables and columns (NO domains yet)
  if (step === 'table-selection-done' || step === 'domain-classification' || step === 'domain-manual') {
    let tableId = 2000;
    let columnId = 30000;
    let linkId = 5000;
    
    // Show all selected tables during manual domain entry
    let tablesToShow = selectedTablesData;
    
    // For each table to show, create table node and column nodes
    tablesToShow.forEach((table, tIndex) => {
      // Get columns from mock data or generate defaults
      const tableName = table.name || `Table ${tIndex + 1}`;
      const matchingTableData = Object.values(autoDataStructure).find(domain => 
        Object.keys(domain).some(t => t.toLowerCase().includes(tableName.toLowerCase()))
      );
      
      let columns = [];
      if (matchingTableData) {
        const matchingTable = Object.keys(matchingTableData).find(t => 
          t.toLowerCase().includes(tableName.toLowerCase())
        );
        columns = matchingTableData[matchingTable] || [];
      }
      
      // If no match, generate generic columns
      if (columns.length === 0) {
        columns = Array.from({ length: Math.floor(Math.random() * 8) + 5 }, (_, i) => 
          `Column ${i + 1}`
        );
      }
      
      // Create table node (no domain, so no parent)
      const tableColor = "#64748B"; // Neutral gray for unclassified tables
      nodes.push({
        id: tableId,
        name: tableName,
        description: `${tableName} table`,
        type: "TABLE",
        parent_id: null, // No domain yet
        payload: { 
          id: tableId, 
          name: tableName, 
          node_type: "TABLE",
          color: tableColor
        }
      });
      
      const currentTableId = tableId;
      tableId++;
      
      // Create column nodes
      columns.forEach((columnName) => {
        nodes.push({
          id: columnId,
          name: columnName,
          description: `${columnName} column`,
          type: "COLUMN",
          parent_id: currentTableId,
          payload: { 
            id: columnId, 
            name: columnName, 
            node_type: "COLUMN",
            datatype: columnName.toLowerCase().includes('id') ? "integer" : "string",
            profile: [100, 100, 0, 1.0],
            color: tableColor
          }
        });
        columnId++;
      });
    });
    
    return { nodes, links };
  }

  // STEP 3: After domain classification - show full hierarchy (or progressive during 'classifying' step)
  if (step === 'ready-to-generate' || step === 'classifying' || step === 'generating' || step === 'relationship-identification' || step === 'business-glossary' || step === 'graph' || step === 'completed' || step === 'complete') {
    let domainId = 1000;
    let tableId = 2000;
    let columnId = 30000;
    let linkId = 5000;
    
    const domainIds = {};
    let dataStructure = {};
    
    if (isAutoDomain) {
      // Use automatic domain classification from mock data
      dataStructure = autoDataStructure;
    } else if (domainClassification && domainClassification.length > 0) {
      // Use manual domain classification from user input
      const manualDataStructure = {};
      
      // Filter out empty domains and build structure
      domainClassification
        .filter(domain => domain.name && domain.tables.length > 0)
        .forEach(domain => {
          manualDataStructure[domain.name] = {};
          
          // For each table ID assigned to this domain, find the table and its columns
          domain.tables.forEach(tableId => {
            // Find the table from selectedTablesData
            const selectedTable = selectedTablesData.find(t => t.id === tableId);
            if (selectedTable) {
              const tableName = selectedTable.name;
              
              // Get columns from autoDataStructure for this table
              let columns = [];
              Object.values(autoDataStructure).forEach(domainTables => {
                Object.entries(domainTables).forEach(([autoTableName, autoColumns]) => {
                  if (autoTableName === tableName) {
                    columns = autoColumns;
                  }
                });
              });
              
              // If no match found, generate default columns
              if (columns.length === 0) {
                columns = Array.from({ length: Math.floor(Math.random() * 8) + 5 }, (_, i) => 
                  `Column ${i + 1}`
                );
              }
              
              manualDataStructure[domain.name][tableName] = columns;
            }
          });
        });
      
      dataStructure = manualDataStructure;
    }
    
    // Create domains and store their colors
    const assignedDomainColors = {}; // Store colors for each domain
    
    Object.keys(dataStructure).forEach((domainName, index) => {
      const domainColor = getDomainColor(domainName, index);
      assignedDomainColors[domainName] = domainColor; // Store for later use
      
      domainIds[domainName] = domainId;
      nodes.push({
        id: domainId,
        name: domainName,
        description: `${domainName} domain`,
        type: "DOMAIN",
        parent_id: null,
        payload: { 
          id: domainId, 
          name: domainName, 
          node_type: "DOMAIN",
          color: domainColor
        }
      });
      domainId++;
    });

    // Create tables and columns using stored domain colors
    // During 'classifying' step, only show tables up to classifiedTableCount
    let tableIndex = 0;
    const isClassifying = step === 'classifying' && classifiedTableCount !== null;
    
    // Map to store column nodes by table name and column name for relationship creation
    const columnNodeMap = {};
    
    Object.entries(dataStructure).forEach(([domainName, tables]) => {
      const currentDomainId = domainIds[domainName];
      const domainColor = assignedDomainColors[domainName]; // Use stored color
      
      Object.entries(tables).forEach(([tableName, columns]) => {
        // If we're in classifying mode and we've shown enough tables, skip the rest
        if (isClassifying && tableIndex >= classifiedTableCount) {
          return;
        }
        tableIndex++;
        
        // Create table node
        nodes.push({
          id: tableId,
          name: tableName,
          description: `${tableName} table in ${domainName}`,
          type: "TABLE",
          parent_id: currentDomainId,
          payload: { 
            id: tableId, 
            name: tableName, 
            node_type: "TABLE",
            color: domainColor
          }
        });
        
        const currentTableId = tableId;
        tableId++;
        
        // Initialize column map for this table
        if (!columnNodeMap[tableName]) {
          columnNodeMap[tableName] = {};
        }
        
        // Create column nodes
        columns.forEach((columnName) => {
          nodes.push({
            id: columnId,
            name: columnName,
            description: `${columnName} column`,
            type: "COLUMN",
            parent_id: currentTableId,
            payload: { 
              id: columnId, 
              name: columnName, 
              node_type: "COLUMN",
              datatype: columnName.toLowerCase().includes('id') ? "integer" : "string",
              profile: [100, 100, 0, 1.0],
              color: domainColor
            }
          });
          
          // Store column node reference for relationship mapping
          columnNodeMap[tableName][columnName] = columnId;
          
          columnId++;
        });
      });
    });

    // Define specific Primary Key -> Foreign Key relationships
    // Primary Key: Customer Demographics -> C_ID
    const primaryKeyRelationships = [
      // Customer ID relationships
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Campaigns", fkColumn: "Prospect ID", recordsMapped: 856, accuracy: 92 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Customer Hierarchy", fkColumn: "Party ID", recordsMapped: 1243, accuracy: 95 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Delivery_Survey", fkColumn: "Customer ID", recordsMapped: 672, accuracy: 88 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Expense", fkColumn: "Contact ID", recordsMapped: 934, accuracy: 91 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Install Base", fkColumn: "Party ID", recordsMapped: 1089, accuracy: 94 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Logistics", fkColumn: "Cust_ID", recordsMapped: 782, accuracy: 89 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "NPS Survey", fkColumn: "Customer_ID", recordsMapped: 543, accuracy: 87 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Renewals", fkColumn: "Cus ID", recordsMapped: 1156, accuracy: 96 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Returns", fkColumn: "Customer ID", recordsMapped: 421, accuracy: 85 },
      { pkTable: "Customer Demographics", pkColumn: "C_ID", fkTable: "Service Request", fkColumn: "C ID", recordsMapped: 998, accuracy: 93 },
      
      // Product ID relationships - Primary Key: Product Hierarchy -> Product ID
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Campaigns", fkColumn: "Prod_ID", recordsMapped: 723, accuracy: 91 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Delivery_Survey", fkColumn: "Product ID", recordsMapped: 654, accuracy: 89 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Install Base", fkColumn: "Product ID", recordsMapped: 1087, accuracy: 95 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Inventory", fkColumn: "Prod_ID", recordsMapped: 892, accuracy: 93 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Prob_Statement_Issue", fkColumn: "Product_ID", recordsMapped: 534, accuracy: 87 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Product Engg", fkColumn: "Product ID", recordsMapped: 1245, accuracy: 98 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Product Feature", fkColumn: "P_ID", recordsMapped: 1198, accuracy: 97 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Renewals", fkColumn: "Product ID", recordsMapped: 876, accuracy: 92 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Returns", fkColumn: "Product ID", recordsMapped: 445, accuracy: 86 },
      { pkTable: "Product Hierarchy", pkColumn: "Product ID", fkTable: "Service Request", fkColumn: "Prod ID", recordsMapped: 967, accuracy: 94 },
      
      // Order ID relationships - Primary Key: Install Base -> Order ID
      { pkTable: "Install Base", pkColumn: "Order ID", fkTable: "Delivery_Survey", fkColumn: "Odr ID", recordsMapped: 589, accuracy: 90 },
      { pkTable: "Install Base", pkColumn: "Order ID", fkTable: "Logistics", fkColumn: "Od_Contract_ID", recordsMapped: 734, accuracy: 88 },
      { pkTable: "Install Base", pkColumn: "Order ID", fkTable: "Renewals", fkColumn: "Od ID", recordsMapped: 912, accuracy: 94 },
      { pkTable: "Install Base", pkColumn: "Order ID", fkTable: "Returns", fkColumn: "R Order ID", recordsMapped: 356, accuracy: 85 },
      { pkTable: "Install Base", pkColumn: "Order ID", fkTable: "Service Request", fkColumn: "Sales Ord ID", recordsMapped: 823, accuracy: 91 },
      
      // Campaign ID relationships - Primary Key: Campaigns -> Campaign ID
      { pkTable: "Campaigns", pkColumn: "Campaign ID", fkTable: "Campaign Survey", fkColumn: "Camp ID", recordsMapped: 678, accuracy: 93 },
      { pkTable: "Campaigns", pkColumn: "Campaign ID", fkTable: "Website", fkColumn: "Cmgn ID", recordsMapped: 512, accuracy: 89 }
    ];

    // Only create links after relationship identification step is complete
    if (step === 'business-glossary' || step === 'graph' || step === 'completed' || step === 'complete') {
      // Create links based on the defined relationships
      console.log("=== RELATIONSHIP DEBUGGING ===");
      console.log("Column Node Map:", columnNodeMap);
      console.log("Available tables:", Object.keys(columnNodeMap));
      
      primaryKeyRelationships.forEach((relationship) => {
        const pkNodeId = columnNodeMap[relationship.pkTable]?.[relationship.pkColumn];
        const fkNodeId = columnNodeMap[relationship.fkTable]?.[relationship.fkColumn];
        
        console.log(`Checking relationship: ${relationship.pkTable}.${relationship.pkColumn} -> ${relationship.fkTable}.${relationship.fkColumn}`);
        console.log(`  PK Node ID: ${pkNodeId}, FK Node ID: ${fkNodeId}`);
        
        if (pkNodeId && fkNodeId) {
          links.push({
            id: linkId,
            source: pkNodeId,
            target: fkNodeId,
            link_type: "foreign",
            records_mapped: relationship.recordsMapped,
            accuracy: relationship.accuracy
          });
          console.log(`  ✓ Link created with ID: ${linkId}`);
          linkId++;
        } else {
          console.log(`  ✗ Link NOT created - missing node IDs`);
        }
      });
      
      console.log(`Total links created: ${links.length}`);
      console.log("=== END DEBUGGING ===");
    }

    return { nodes, links };
  }
  
  // Default: empty graph
  return { nodes, links: [] };
};

// Custom node right-click render component
const SimpleNodeRightClickRender = ({ node, onClose, opts }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [glossaryText, setGlossaryText] = useState('');
  
  // Get all nodes from nodesById to traverse hierarchy
  const nodesById = opts?.nodesById || {};
  
  // Extract domain and table from node hierarchy
  const getNodeHierarchy = (node) => {
    if (!node) return { domain: 'Unknown', table: 'Unknown' };
    
    let table = 'Unknown';
    let domain = 'Unknown';
    
    const parentId = node.parent_node_id || node.parent_id;
    if (parentId) {
      const parentNode = nodesById[parentId];
      if (parentNode) {
        table = parentNode.name || 'Unknown';
        
        const grandParentId = parentNode.parent_node_id || parentNode.parent_id;
        if (grandParentId) {
          const grandParentNode = nodesById[grandParentId];
          if (grandParentNode) {
            domain = grandParentNode.name || 'Unknown';
          }
        }
      }
    }
    
    return { domain, table };
  };
  
  const hierarchy = getNodeHierarchy(node);
  const columnName = node?.name || 'Unknown';
  
  // Generate mock metrics
  const metrics = React.useMemo(() => ({
    count: `${(Math.random() * 10 + 0.5).toFixed(1)}K`,
    completeness: (85 + Math.random() * 15).toFixed(1),
    uniqueness: (Math.random() * 50).toFixed(1)
  }), [node?.node_id]);
  
  // Mock sample data
  const sampleData = ['NT30', 'NET45', '2/10NET30', 'COD', 'PREPAID', 'NET60', '1/15NET30'];
  
  // Mock tags
  const tags = ['Payment', 'Transaction', 'Code'];
  
  return (
    <div className="w-[300px] rounded-[10px] overflow-hidden shadow-xl z-50" style={{ background: 'rgba(30, 41, 59, 0.95)', border: '1px solid rgba(51, 65, 85, 0.3)' }}>
      {/* Header */}
      <div className="flex justify-between items-start p-[10px_12px] border-b border-slate-700/30" style={{ background: '#020617' }}>
        <div className="flex items-center gap-[10px]">
          <div className="w-7 h-7 rounded-[6px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <BookOpen className="w-3 h-3 text-blue-400" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-gray-100 mb-0.5">{columnName}</div>
            <div className="text-[9px] text-gray-500">
              <span className="text-blue-500">{hierarchy.domain}</span> › {hierarchy.table}
            </div>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:bg-slate-700/40 hover:text-gray-200 transition-all"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      
      {/* Body */}
      <div className="p-[10px] relative">
        {/* Preview Panel Overlay */}
        {showPreview && (
          <div className="absolute inset-0 z-20 p-3 flex flex-col" style={{ background: '#1e293b' }}>
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400">
                Sample Values ({columnName})
              </span>
              <button 
                onClick={() => setShowPreview(false)} 
                className="text-gray-500 hover:text-gray-200 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
              {sampleData.map((val, i) => (
                <div key={i} className="text-[11px] text-slate-300 font-mono px-2.5 py-1.5 rounded" style={{ background: '#0f172a', border: '1px solid #334155' }}>
                  {val}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Properties Card */}
        <div className="rounded-[6px] p-[8px_10px] mb-2" style={{ background: '#0f172a' }}>
          <div className="text-[8px] font-semibold uppercase tracking-[0.5px] text-gray-500 mb-2">Properties</div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-gray-400">Data Type</span>
            <div className="flex items-center justify-between gap-1 px-2 py-1 rounded text-[10px] text-gray-200 min-w-[100px] cursor-pointer" style={{ background: '#1e293b', border: '0.5px solid rgba(71, 85, 105, 0.4)' }}>
              <span>alphanumeric</span>
              <ChevronDown className="w-3 h-3 text-gray-500" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400">Contains PII</span>
            <div className="w-7 h-4 rounded-full relative cursor-pointer" style={{ background: '#334155' }}>
              <div className="absolute w-3 h-3 bg-gray-200 rounded-full top-0.5 left-0.5" />
            </div>
          </div>
        </div>
        
        {/* Business Context Card */}
        <div className="rounded-[6px] p-[8px_10px] mb-2" style={{ background: '#0f172a' }}>
          <div className="text-[8px] font-semibold uppercase tracking-[0.5px] text-gray-500 mb-2">Business Context</div>
          <textarea 
            className="w-full rounded p-1.5 text-[10px] text-slate-300 leading-[1.4] resize-none h-[42px] outline-none transition-colors focus:border-blue-500"
            style={{ background: '#1e293b', border: '0.5px solid rgba(71, 85, 105, 0.4)' }}
            placeholder="Generating glossary..."
            value={glossaryText}
            onChange={(e) => setGlossaryText(e.target.value)}
          />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {tags.map((tag, i) => (
              <span key={i} className="px-2 py-0.5 rounded-[10px] text-[9px] text-slate-300" style={{ background: 'rgba(51, 65, 85, 0.4)', border: '1px solid rgba(71, 85, 105, 0.4)' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
        
        {/* Quality Metrics Card */}
        <div className="rounded-[6px] p-[8px_10px]" style={{ background: '#0f172a' }}>
          <div className="text-[8px] font-semibold uppercase tracking-[0.5px] text-gray-500 mb-2">Quality Metrics</div>
          <div className="flex gap-2">
            <div className="flex-1 text-center">
              <div className="text-[14px] font-bold text-gray-100">{metrics.count}</div>
              <div className="text-[8px] text-gray-500">Count</div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-[14px] font-bold text-gray-100">{metrics.completeness}%</div>
              <div className="text-[8px] text-gray-500">Complete</div>
              <div className="h-0.5 rounded mt-1 overflow-hidden" style={{ background: '#334155' }}>
                <div className="h-full bg-blue-500 rounded" style={{ width: `${metrics.completeness}%` }} />
              </div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-[14px] font-bold text-gray-100">{metrics.uniqueness}%</div>
              <div className="text-[8px] text-gray-500">Unique</div>
              <div className="h-0.5 rounded mt-1 overflow-hidden" style={{ background: '#334155' }}>
                <div className="h-full bg-gray-500 rounded" style={{ width: `${metrics.uniqueness}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/40" style={{ background: '#020617' }}>
        <button 
          onClick={() => setShowPreview(!showPreview)}
          className="w-full flex items-center justify-center gap-1.5 text-gray-400 text-[11px] font-medium hover:bg-slate-800/40 hover:text-gray-200 transition-all"
          style={{ padding: '10px 14px' }}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview Data
        </button>
      </div>
    </div>
  );
};

// Custom link right-click render component
// Custom link right-click render component - AI Link Prediction Popup
const SimpleLinkRightClickRender = ({ link, onClose, opts }) => {
  const [activePreview, setActivePreview] = useState(null); // 'source' | 'target' | null
  
  // Get source and target node details
  const sourceNode = typeof link.source === 'object' ? link.source : null;
  const targetNode = typeof link.target === 'object' ? link.target : null;
  
  // Get all nodes from nodesById to traverse hierarchy
  const nodesById = opts?.nodesById || {};
  
  // Extract details from nodes by traversing hierarchy
  const getNodeDetails = (node) => {
    if (!node) return { domain: 'Unknown', table: 'Unknown', column: 'Unknown' };
    
    const column = node.name || 'Unknown';
    let table = 'Unknown';
    let domain = 'Unknown';
    
    // Node is a column - find parent table using parent_node_id
    const parentId = node.parent_node_id || node.parent_id;
    if (parentId) {
      const parentNode = nodesById[parentId];
      if (parentNode) {
        table = parentNode.name || 'Unknown';
        
        // Parent is a table - find grandparent domain
        const grandParentId = parentNode.parent_node_id || parentNode.parent_id;
        if (grandParentId) {
          const grandParentNode = nodesById[grandParentId];
          if (grandParentNode) {
            domain = grandParentNode.name || 'Unknown';
          }
        }
      }
    }
    
    // Fallback to payload if available
    if (domain === 'Unknown' && node.payload?.domain) domain = node.payload.domain;
    if (table === 'Unknown' && node.payload?.table) table = node.payload.table;
    if (table === 'Unknown' && node.payload?.parent_name) table = node.payload.parent_name;
    
    return { domain, table, column };
  };
  
  const sourceDetails = getNodeDetails(sourceNode);
  const targetDetails = getNodeDetails(targetNode);
  
  // Get relationship details from payload
  const payload = link.payload?.[0] || {};
  const recordsMapped = payload.records_mapped || link.records_mapped || 0;
  const accuracy = link.accuracy || 94;
  
  // Generate randomized mapped percentages - one >90%, one 30-80%
  const randomizedMapped = React.useMemo(() => {
    const highMapped = (90 + Math.random() * 10).toFixed(1); // 90-100%
    const lowMapped = (30 + Math.random() * 50).toFixed(1);  // 30-80%
    // Randomly assign which side gets the high value
    const sourceHigh = Math.random() > 0.5;
    return {
      source: sourceHigh ? highMapped : lowMapped,
      target: sourceHigh ? lowMapped : highMapped
    };
  }, [link.id]);
  
  // Generate mock metrics for display
  const generateMetrics = (isSource) => {
    const baseCount = isSource ? 66.7 : 259.5;
    const mapped = isSource ? randomizedMapped.source : randomizedMapped.target;
    return {
      count: `${baseCount}M`,
      uniqueness: isSource ? '100%' : '0.0%',
      mapped: `${mapped}%`,
      completeness: '100%',
      connections: isSource ? 9 : 1
    };
  };
  
  const sourceMetrics = generateMetrics(true);
  const targetMetrics = generateMetrics(false);
  
  // Get mapped color class based on percentage
  const getMappedColorClass = (mappedStr) => {
    const value = parseFloat(mappedStr);
    if (value >= 70) return { border: 'border-l-green-400', text: 'text-green-400', bgStyle: 'linear-gradient(90deg, rgba(74, 222, 128, 0.08) 0%, transparent 100%)' };
    if (value >= 40) return { border: 'border-l-amber-400', text: 'text-amber-400', bgStyle: 'linear-gradient(90deg, rgba(251, 191, 36, 0.08) 0%, transparent 100%)' };
    return { border: 'border-l-red-400', text: 'text-red-400', bgStyle: 'linear-gradient(90deg, rgba(248, 113, 113, 0.08) 0%, transparent 100%)' };
  };
  
  const sourceColor = getMappedColorClass(sourceMetrics.mapped);
  const targetColor = getMappedColorClass(targetMetrics.mapped);
  
  // Sample data for preview
  const sampleSourceData = ['0080012345', '0080012346', '0080012347', '0080012348', '0080012349'];
  const sampleTargetData = ['SO-2024-00142', 'SO-2024-00143', 'SO-2024-00144', 'SO-2024-00145', 'SO-2024-00146'];
  
  const togglePreview = (type) => {
    setActivePreview(prev => prev === type ? null : type);
  };
  
  return (
    <div className="rounded-lg border border-slate-700/50 w-[540px] overflow-hidden shadow-xl z-50" style={{ background: 'rgba(30, 41, 59, 0.95)' }}>
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-700/40" style={{ padding: '10px 14px', background: 'rgba(2, 6, 23, 0.5)' }}>
        <div className="flex items-center gap-[10px]">
          <div className="w-7 h-7 rounded-[6px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <Link2 className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="flex items-center gap-[5px] text-[13px] font-semibold text-gray-200 font-geist">
            <span>Data Relationship</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <span>Accuracy:</span>
            <span className="text-gray-200 font-semibold">{accuracy}%</span>
            <div className="w-9 h-[3px] bg-slate-700/60 rounded overflow-hidden">
              <div className="h-full bg-green-400 rounded" style={{ width: `${accuracy}%` }} />
            </div>
          </div>
          <button 
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:text-gray-200 hover:bg-slate-700/40 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Body wrapper for preview overlay */}
      <div className="relative">
        {/* Grey out overlay for opposite side */}
        {activePreview === 'source' && (
          <div className="absolute inset-y-0 left-1/2 right-0 bg-slate-900/60 z-10 pointer-events-none" />
        )}
        {activePreview === 'target' && (
          <div className="absolute inset-y-0 left-0 right-1/2 bg-slate-900/60 z-10 pointer-events-none" />
        )}
        
        {/* Sample Data Panels */}
        {activePreview === 'source' && (
          <div className="absolute inset-y-0 left-0 right-1/2 bg-slate-800 z-20 border-r border-slate-700/40 flex flex-col" style={{ padding: '12px 14px' }}>
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400">
                Sample Values ({sourceDetails.column})
              </span>
              <button onClick={() => togglePreview('source')} className="text-gray-400 hover:text-gray-200 p-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
              {sampleSourceData.map((val, i) => (
                <div key={i} className="text-xs text-slate-300 font-mono px-2 py-1 bg-slate-900/50 rounded border border-slate-700/30">
                  {val}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activePreview === 'target' && (
          <div className="absolute inset-y-0 left-1/2 right-0 bg-slate-800 z-20 flex flex-col" style={{ padding: '12px 14px' }}>
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400">
                Sample Values ({targetDetails.column})
              </span>
              <button onClick={() => togglePreview('target')} className="text-gray-400 hover:text-gray-200 p-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
              {sampleTargetData.map((val, i) => (
                <div key={i} className="text-xs text-slate-300 font-mono px-2 py-1 bg-slate-900/50 rounded border border-slate-700/30">
                  {val}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Two Column Content */}
        <div className="grid grid-cols-2">
          {/* Source Column */}
          <div className="border-r border-slate-700/40" style={{ padding: '12px 14px' }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400 border-b-2 border-blue-500" style={{ marginBottom: '10px', paddingBottom: '6px' }}>
              Source
            </div>
            
            {/* Info Grid */}
            <div className="grid grid-cols-3" style={{ gap: '8px', marginBottom: '10px' }}>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Domain</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{sourceDetails.domain}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Table</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{sourceDetails.table}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Column</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{sourceDetails.column}</div>
              </div>
            </div>
            
            <div className="h-px bg-slate-700/40" style={{ margin: '10px 0' }} />
            
            {/* Mapped - Primary Metric */}
            <div 
              className={`flex items-center justify-between border-l-[3px] ${sourceColor.border}`}
              style={{ padding: '4px 0 4px 10px', marginBottom: '8px', background: sourceColor.bgStyle }}
            >
              <span className="text-gray-400 text-[11px]">Mapped</span>
              <span className={`text-[13px] font-bold ${sourceColor.text}`}>{sourceMetrics.mapped}</span>
            </div>
            
            {/* Other Metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Count</span>
                <span className="text-gray-200 text-xs font-semibold">{sourceMetrics.count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Uniqueness</span>
                <span className="text-green-400 text-xs font-semibold">{sourceMetrics.uniqueness}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Completeness</span>
                <span className="text-green-400 text-xs font-semibold">{sourceMetrics.completeness}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Connections</span>
                <span className="text-gray-200 text-xs font-semibold">{sourceMetrics.connections}</span>
              </div>
            </div>
          </div>
          
          {/* Target Column */}
          <div style={{ padding: '12px 14px' }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400 border-b-2 border-green-500" style={{ marginBottom: '10px', paddingBottom: '6px' }}>
              Target
            </div>
            
            {/* Info Grid */}
            <div className="grid grid-cols-3" style={{ gap: '8px', marginBottom: '10px' }}>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Domain</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{targetDetails.domain}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Table</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{targetDetails.table}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-400 mb-0.5">Column</div>
                <div className="text-gray-200 text-[11px] font-medium truncate">{targetDetails.column}</div>
              </div>
            </div>
            
            <div className="h-px bg-slate-700/40" style={{ margin: '10px 0' }} />
            
            {/* Mapped - Primary Metric */}
            <div 
              className={`flex items-center justify-between border-l-[3px] ${targetColor.border}`}
              style={{ padding: '4px 0 4px 10px', marginBottom: '8px', background: targetColor.bgStyle }}
            >
              <span className="text-gray-400 text-[11px]">Mapped</span>
              <span className={`text-[13px] font-bold ${targetColor.text}`}>{targetMetrics.mapped}</span>
            </div>
            
            {/* Other Metrics */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Count</span>
                <span className="text-gray-200 text-xs font-semibold">{targetMetrics.count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Uniqueness</span>
                <span className="text-gray-200 text-xs font-semibold">{targetMetrics.uniqueness}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Completeness</span>
                <span className="text-green-400 text-xs font-semibold">{targetMetrics.completeness}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[11px]">Connections</span>
                <span className="text-gray-200 text-xs font-semibold">{targetMetrics.connections}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="grid grid-cols-2 border-t border-slate-700/40" style={{ background: 'rgba(2, 6, 23, 0.5)' }}>
        <button 
          onClick={() => togglePreview('source')}
          className="flex items-center justify-center gap-1.5 text-gray-400 text-[11px] font-medium hover:bg-slate-800/40 hover:text-gray-200 transition-all border-r border-slate-700/40"
          style={{ padding: '10px 14px' }}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview Source
        </button>
        <button 
          onClick={() => togglePreview('target')}
          className="flex items-center justify-center gap-1.5 text-gray-400 text-[11px] font-medium hover:bg-slate-800/40 hover:text-gray-200 transition-all"
          style={{ padding: '10px 14px' }}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview Target
        </button>
      </div>
    </div>
  );
};


const Dashboard = () => {
  const navigate = useNavigate();
  const [expandedConnections, setExpandedConnections] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConnectionsPanel, setShowConnectionsPanel] = useState(false);
  
  // Physics parameters state for graph layout control (SIMPLIFIED)
  const [physicsParams, setPhysicsParams] = useState({
    domainSpacing: 800,
    tableRadius: 200,
    columnRadius: 80,
    snapBackStrength: 0.15,
    enableSnapBack: false,
    velocityDecay: 0.6,
    collisionStrength: 0.3,
    tableForceStrength: 0.1,   // Minimum for smoothest movement
    columnForceStrength: 0.4,
  });
  
  // Resizable layout state
  const [leftWidth, setLeftWidth] = useState(30); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [collapsedPanel, setCollapsedPanel] = useState(null); // null, 'left', or 'right'
  const [preCollapseWidth, setPreCollapseWidth] = useState(30); // Store width before collapse
  
  // Memoize canvas dimensions to prevent re-renders on typing
  const canvasDimensions = useMemo(() => ({
    width: window.innerWidth * ((100 - leftWidth) / 100),
    height: window.innerHeight
  }), [leftWidth]);

  // Collapse handlers
  const collapseLeft = () => {
    if (collapsedPanel === 'left') {
      // Restore
      setLeftWidth(preCollapseWidth);
      setCollapsedPanel(null);
    } else {
      // Collapse left panel
      setPreCollapseWidth(leftWidth);
      setLeftWidth(0);
      setCollapsedPanel('left');
    }
  };

  const collapseRight = () => {
    if (collapsedPanel === 'right') {
      // Restore
      setLeftWidth(preCollapseWidth);
      setCollapsedPanel(null);
    } else {
      // Collapse right panel
      setPreCollapseWidth(leftWidth);
      setLeftWidth(100);
      setCollapsedPanel('right');
    }
  };
  
  // Industries list
  const industries = [
    'E-commerce',
    'Healthcare',
    'Finance & Banking',
    'Retail',
    'Manufacturing',
    'Technology & SaaS',
    'Education',
    'Real Estate',
    'Logistics & Supply Chain',
    'Media & Entertainment',
    'Telecommunications',
    'Energy & Utilities',
    'Travel & Hospitality',
    'Insurance',
    'Automotive',
    'Pharmaceutical',
    'Agriculture',
    'Government & Public Sector',
    'Non-Profit',
    'Other'
  ];

  // Map currentStep to progress pill step number (1-6)
  const getProgressStep = (step) => {
    switch (step) {
      case 'table-selection':
      case 'table-selection-done':
        return 1; // Table Selection
      case 'context':
        return 2; // Business Context
      case 'domain-classification':
      case 'domain-manual':
      case 'ready-to-generate':
        return 3; // Domain Classification
      case 'classifying':
        return 3; // Domain Classification (in progress)
      case 'generating':
        return 4; // Data Profiling
      case 'relationship-identification':
        return 5; // Relationship Identification
      case 'business-glossary':
        return 6; // Business Glossary (in progress)
      case 'graph':
      case 'complete':
      case 'completed':
        return 6; // Business Glossary (Final/Completed)
      default:
        return 0; // Not started
    }
  };

  // Agent configuration with colors
  const agents = {
    dataApps: [
      { id: 'data-products', name: 'Data Products', icon: '📦', description: 'Manage data products', color: '#FF8844' },
      { id: 'dashboards', name: 'Dashboards', icon: '📊', description: 'Create dashboards', color: '#C688FF' },
      { id: 'chatbots', name: 'Chatbots', icon: '💬', description: 'Conversational interface', color: '#FF88D4' },
      { id: 'ai-models', name: 'AI Models', icon: '🤖', description: 'Build and deploy AI models', color: '#88FFD4' }
    ],
    dataDiscovery: [
      { id: 'semantic-graph', name: 'Semantic Data Graph', icon: '🕸️', description: 'Generate semantic data graphs', color: '#60a5fa' },
      { id: 'data-transformation', name: 'Data Transformation', icon: '🔄', description: 'Transform and process data', color: '#4DFFB4' },
      { id: 'data-analysis', name: 'Data Analysis', icon: '🔍', description: 'Analyze and explore data', color: '#FF6B9D' }
    ]
  };
  
  // Agent status - determine if agent is "running" or "waiting"
  // Syncs with step box status: running when Processing, waiting when Waiting or Done
  const getAgentStatus = () => {
    // Agent is "running" only when actively processing (step box shows "Processing")
    if (isProcessing) {
      return 'running';
    }
    // Otherwise waiting (step box shows "Waiting" or "Done")
    return 'waiting';
  };

  // Session management state (only for insights generator)
  const [sessions, setSessions] = useState([
    { id: 'session-1', name: 'Data Analysis Q1 2024', messages: [], createdAt: new Date(), lastUpdated: new Date() },
    { id: 'session-2', name: 'Sales Performance Review', messages: [], createdAt: new Date(), lastUpdated: new Date() },
    { id: 'session-3', name: 'Customer Segmentation', messages: [], createdAt: new Date(), lastUpdated: new Date() }
  ]);
  const [currentSession, setCurrentSession] = useState(sessions[0]);
  const [showSessionSidebar, setShowSessionSidebar] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  
  // LLM Models
  const llmModels = [
    { id: 'gpt-4', name: 'GPT-4', provider: 'OpenAI' },
    { id: 'gpt-3.5', name: 'GPT-3.5 Turbo', provider: 'OpenAI' },
    { id: 'claude-3', name: 'Claude 3 Opus', provider: 'Anthropic' },
    { id: 'claude-2', name: 'Claude 2', provider: 'Anthropic' },
    { id: 'gemini-pro', name: 'Gemini Pro', provider: 'Google' },
  ];

  // Chat state
  const [currentAgent, setCurrentAgent] = useState(agents.dataDiscovery[0]); // Default to Semantic Data Graph
  const [currentModel, setCurrentModel] = useState(llmModels[0]); // Default to GPT-4
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Processing state for typing indicator
  const [processingStepText, setProcessingStepText] = useState(''); // Current processing step to show in typing indicator
  const agentDropdownRef = React.useRef(null);
  const modelDropdownRef = React.useRef(null);
  const agentButtonRef = React.useRef(null);
  const modelButtonRef = React.useRef(null);
  const [agentDropdownPosition, setAgentDropdownPosition] = useState({ bottom: 0, left: 0 });
  const [modelDropdownPosition, setModelDropdownPosition] = useState({ bottom: 0, left: 0 });
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: "Hi! 👋 I'm your Semantic Data Graph agent. Let's build your semantic data graph together.",
      type: 'text'
    },
    {
      id: 2,
      role: 'assistant',
      type: 'table-selection-browse',
      status: 'completed',
      totalTables: 19,
      introText: `All 19 tables are pre-selected. You can modify the selection if needed:`
    },
    {
      id: 3,
      role: 'assistant',
      type: 'context-form'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = React.useRef(null);
  const messagesContainerRef = React.useRef(null);
  
  // Scope limiting state
  const [showScopePanel, setShowScopePanel] = useState(false);
  const [scopePanelMode, setScopePanelMode] = useState('floating'); // 'floating' or 'accordion'
  const [scopeActiveTab, setScopeActiveTab] = useState('domains');
  const [selectedScopeDomains, setSelectedScopeDomains] = useState([]);
  const [selectedScopeTables, setSelectedScopeTables] = useState([]);
  const [selectedScopeDataApps, setSelectedScopeDataApps] = useState([]);
  const [selectedScopeTags, setSelectedScopeTags] = useState([]);
  const [scopeSearchQuery, setScopeSearchQuery] = useState('');
  const scopePanelRef = React.useRef(null);
  const scopeButtonRef = React.useRef(null);
  const [scopePanelPosition, setScopePanelPosition] = useState({ bottom: 58, left: 0 });
  
  // @mention state
  const [showMentionPanel, setShowMentionPanel] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [mentionActiveTab, setMentionActiveTab] = useState('tables');
  const [mentionPanelPosition, setMentionPanelPosition] = useState({ bottom: 0, left: 0 });
  const [selectedMentions, setSelectedMentions] = useState([]); // Track selected @mentions
  const textareaRef = React.useRef(null);
  const mentionPanelRef = React.useRef(null);
  
  // Update mention panel position when shown - positioned at @ symbol
  React.useEffect(() => {
    if (showMentionPanel && textareaRef.current && mentionStartIndex >= 0) {
      const textarea = textareaRef.current;
      const rect = textarea.getBoundingClientRect();
      
      // Create a mirror div to measure text position
      const mirror = document.createElement('div');
      const style = window.getComputedStyle(textarea);
      
      // Copy textarea styles to mirror
      mirror.style.position = 'absolute';
      mirror.style.visibility = 'hidden';
      mirror.style.whiteSpace = 'pre-wrap';
      mirror.style.wordWrap = 'break-word';
      mirror.style.width = style.width;
      mirror.style.fontSize = style.fontSize;
      mirror.style.fontFamily = style.fontFamily;
      mirror.style.lineHeight = style.lineHeight;
      mirror.style.padding = style.padding;
      
      // Get text up to @ symbol and add a span to measure position
      const textBefore = inputMessage.substring(0, mentionStartIndex);
      mirror.innerHTML = textBefore.replace(/\n/g, '<br>') + '<span id="caret"></span>';
      document.body.appendChild(mirror);
      
      const caretSpan = mirror.querySelector('#caret');
      const caretRect = caretSpan.getBoundingClientRect();
      const mirrorRect = mirror.getBoundingClientRect();
      
      // Calculate offset from mirror
      const offsetLeft = caretRect.left - mirrorRect.left;
      const offsetTop = caretRect.top - mirrorRect.top;
      
      document.body.removeChild(mirror);
      
      setMentionPanelPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left + offsetLeft
      });
    }
  }, [showMentionPanel, mentionStartIndex, inputMessage]);
  
  // Update scope panel position when button is clicked
  React.useEffect(() => {
    if (showScopePanel && scopeButtonRef.current) {
      const rect = scopeButtonRef.current.getBoundingClientRect();
      setScopePanelPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.right + 8
      });
    }
  }, [showScopePanel]);
  
  // Update agent dropdown position when opened
  React.useEffect(() => {
    if (showAgentDropdown && agentButtonRef.current) {
      const rect = agentButtonRef.current.getBoundingClientRect();
      setAgentDropdownPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left
      });
    }
  }, [showAgentDropdown]);
  
  // Update model dropdown position when opened
  React.useEffect(() => {
    if (showModelDropdown && modelButtonRef.current) {
      const rect = modelButtonRef.current.getBoundingClientRect();
      setModelDropdownPosition({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left
      });
    }
  }, [showModelDropdown]);
  
  // Domain colors for scope panel (matching the graph colors)
  const scopeDomainColors = {
    "Marketing": "#FF4D4D",
    "Customer": "#4DFFFF",
    "Supply Chain": "#4DFFB4",
    "Sales & Orders": "#FFB84D",
    "Support & Services": "#C688FF",
    "Products": "#4DB4FF"
  };
  
  // Get all domains with their table counts
  const scopeDomains = useMemo(() => {
    return Object.entries(autoDataStructure).map(([name, tables]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      tableCount: Object.keys(tables).length,
      columnCount: Object.values(tables).reduce((sum, cols) => sum + cols.length, 0),
      color: scopeDomainColors[name] || '#64748b'
    }));
  }, []);
  
  // Get all tables with their domain info
  const scopeTables = useMemo(() => {
    const tables = [];
    Object.entries(autoDataStructure).forEach(([domainName, domainTables]) => {
      Object.entries(domainTables).forEach(([tableName, columns]) => {
        tables.push({
          id: tableName.toLowerCase().replace(/\s+/g, '-'),
          name: tableName,
          domain: domainName,
          columnCount: columns.length,
          color: scopeDomainColors[domainName] || '#64748b'
        });
      });
    });
    return tables;
  }, []);
  
  // Mock data for Data Apps
  const scopeDataApps = useMemo(() => [
    { id: 'customer-360', name: 'Customer 360', description: 'Complete customer view', tableCount: 8, color: '#4DFFFF' },
    { id: 'sales-analytics', name: 'Sales Analytics', description: 'Revenue & pipeline insights', tableCount: 5, color: '#FFB84D' },
    { id: 'inventory-tracker', name: 'Inventory Tracker', description: 'Stock management app', tableCount: 4, color: '#4DFFB4' },
    { id: 'marketing-insights', name: 'Marketing Insights', description: 'Campaign performance', tableCount: 6, color: '#FF4D4D' },
    { id: 'supply-chain-monitor', name: 'Supply Chain Monitor', description: 'Logistics dashboard', tableCount: 7, color: '#C688FF' }
  ], []);
  
  // Mock data for Tags
  const scopeTags = useMemo(() => [
    { id: 'pii', name: 'PII', description: 'Personal identifiable info', count: 12, color: '#FF4D4D' },
    { id: 'financial', name: 'Financial', description: 'Financial data', count: 8, color: '#4DFFB4' },
    { id: 'confidential', name: 'Confidential', description: 'Restricted access', count: 5, color: '#FFB84D' },
    { id: 'public', name: 'Public', description: 'Publicly accessible', count: 15, color: '#4DB4FF' },
    { id: 'deprecated', name: 'Deprecated', description: 'Legacy data', count: 3, color: '#64748b' },
    { id: 'core-metric', name: 'Core Metric', description: 'Key business metrics', count: 9, color: '#C688FF' }
  ], []);
  
  // Toggle domain selection
  const toggleScopeDomain = (domainId) => {
    setSelectedScopeDomains(prev => 
      prev.includes(domainId) 
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };
  
  // Toggle table selection
  const toggleScopeTable = (tableId) => {
    setSelectedScopeTables(prev => 
      prev.includes(tableId) 
        ? prev.filter(id => id !== tableId)
        : [...prev, tableId]
    );
  };
  
  // Toggle data app selection
  const toggleScopeDataApp = (appId) => {
    setSelectedScopeDataApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };
  
  // Toggle tag selection
  const toggleScopeTag = (tagId) => {
    setSelectedScopeTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };
  
  // Clear all scope selections
  const clearScope = () => {
    setSelectedScopeDomains([]);
    setSelectedScopeTables([]);
    setSelectedScopeDataApps([]);
    setSelectedScopeTags([]);
  };
  
  // Get total selected count
  const scopeSelectedCount = selectedScopeDomains.length + selectedScopeTables.length + selectedScopeDataApps.length + selectedScopeTags.length;
  
  // @mention data - combine tables and data products
  const mentionItems = useMemo(() => {
    const items = [];
    // Add tables
    scopeTables.forEach(table => {
      items.push({
        id: `table-${table.id}`,
        name: table.name,
        type: 'table',
        description: `${table.domain} • ${table.columnCount} cols`,
        color: table.color
      });
    });
    // Add data apps/products
    scopeDataApps.forEach(app => {
      items.push({
        id: `app-${app.id}`,
        name: app.name,
        type: 'app',
        description: app.description,
        color: app.color
      });
    });
    return items;
  }, [scopeTables, scopeDataApps]);
  
  // Filter mention items based on search and active tab
  const filteredMentionItems = useMemo(() => {
    let items = mentionItems;
    if (mentionActiveTab === 'tables') {
      items = items.filter(item => item.type === 'table');
    } else if (mentionActiveTab === 'apps') {
      items = items.filter(item => item.type === 'app');
    }
    if (mentionSearchQuery) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(mentionSearchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(mentionSearchQuery.toLowerCase())
      );
    }
    return items;
  }, [mentionItems, mentionActiveTab, mentionSearchQuery]);
  
  // Handle input change with @mention detection
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInputMessage(value);
    
    // Check for @ symbol
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = atIndex > 0 ? value[atIndex - 1] : ' ';
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) {
        const searchText = textBeforeCursor.substring(atIndex + 1);
        // Only show panel if no space after the search text (still typing the mention)
        if (!searchText.includes(' ')) {
          setMentionStartIndex(atIndex);
          setMentionSearchQuery(searchText);
          setShowMentionPanel(true);
          return;
        }
      }
    }
    
    // Hide mention panel if conditions not met
    setShowMentionPanel(false);
    setMentionSearchQuery('');
    setMentionStartIndex(-1);
  };
  
  // Handle mention selection
  const handleMentionSelect = (item) => {
    if (mentionStartIndex === -1) return;
    
    const beforeMention = inputMessage.substring(0, mentionStartIndex);
    const afterMention = inputMessage.substring(mentionStartIndex + 1 + mentionSearchQuery.length);
    const newValue = `${beforeMention}@${item.name} ${afterMention}`;
    
    setInputMessage(newValue);
    
    // Track the selected mention
    setSelectedMentions(prev => {
      // Avoid duplicates
      if (prev.some(m => m.id === item.id)) return prev;
      return [...prev, item];
    });
    
    setShowMentionPanel(false);
    setMentionSearchQuery('');
    setMentionStartIndex(-1);
    
    // Focus back on textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };
  
  // Remove a mention
  const removeMention = (mentionId) => {
    const mention = selectedMentions.find(m => m.id === mentionId);
    if (mention) {
      // Remove from input text
      const regex = new RegExp(`@${mention.name}\\s?`, 'g');
      setInputMessage(prev => prev.replace(regex, ''));
      // Remove from selected mentions
      setSelectedMentions(prev => prev.filter(m => m.id !== mentionId));
    }
  };
  
  // Close mention panel on click outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (mentionPanelRef.current && !mentionPanelRef.current.contains(event.target)) {
        setShowMentionPanel(false);
      }
    };
    
    if (showMentionPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMentionPanel]);
  
  // Helper function to simulate processing delay
  const simulateProcessing = (callback, delay = 10000) => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      callback();
    }, delay);
  };
  
  const handleAgentSwitch = (agent) => {
    setCurrentAgent(agent);
    setShowAgentDropdown(false);
    
    // Add system message about agent switch
    const switchMessage = {
      id: messages.length + 1,
      role: 'assistant',
      content: `Switched to ${agent.icon} ${agent.name}. ${agent.description}.`,
      type: 'text'
    };
    setMessages(prev => [...prev, switchMessage]);
  };

  const handleModelSwitch = (model) => {
    setCurrentModel(model);
    setShowModelDropdown(false);
  };

  const handleFileAttach = () => {
    // TODO: Implement file attachment
    alert('File attachment coming soon!');
  };

  // Handle click outside dropdowns to close them
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setShowModelDropdown(false);
      }
      // Close scope panel if clicking outside (only for floating mode)
      if (scopePanelMode === 'floating' && scopePanelRef.current && !scopePanelRef.current.contains(event.target)) {
        // Check if click is on the scope pill button
        const scopePill = event.target.closest('[data-scope-trigger]');
        if (!scopePill) {
          setShowScopePanel(false);
        }
      }
    };

    if (showAgentDropdown || showModelDropdown || (showScopePanel && scopePanelMode === 'floating')) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAgentDropdown, showModelDropdown, showScopePanel, scopePanelMode]);

  // Generate contextual agent title
  const getAgentTitle = () => {
    switch (currentAgent.id) {
      case 'semantic-graph':
        return 'Semantic Graph Agent';
      case 'data-products':
        return 'Data Products Agent';
      case 'chat-bot':
        return 'Chatbot Configuration Agent';
      case 'dashboards':
        return 'Dashboard Generation Agent';
      case 'data-transformation':
        return 'Data Transformation Agent';
      case 'insights-generator':
        return 'Insights Generation Agent';
      default:
        return 'Data Assistant';
    }
  };
  const getContextualPlaceholder = () => {
    return 'Ask anything • Type @ to reference data';
  };
  
  // Smart auto-scroll based on message length
  React.useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const container = messagesContainerRef.current;
      const lastMessage = messages[messages.length - 1];
      
      setTimeout(() => {
        // For long form messages (forms, table selection, domain entry), scroll more intelligently
        if (lastMessage && (
          lastMessage.type === 'table-selection-browse' || 
          lastMessage.type === 'context-form' ||
          lastMessage.type === 'domain-manual-entry'
        )) {
          // For complex UI messages, scroll to show the top of the message with some padding
          const messageElements = container.querySelectorAll('.space-y-4 > *');
          const lastMessageElement = messageElements[messageElements.length - 1];
          
          if (lastMessageElement) {
            // Calculate position to show the message header with padding
            const containerRect = container.getBoundingClientRect();
            const messageRect = lastMessageElement.getBoundingClientRect();
            const scrollPosition = container.scrollTop + (messageRect.top - containerRect.top) - 80; // 80px padding from top
            
            container.scrollTo({
              top: Math.max(0, scrollPosition),
              behavior: 'smooth'
            });
          }
        } else {
          // For regular text messages, scroll to very bottom
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [messages]);
  
  // Flow state
  const [currentStep, setCurrentStep] = useState('context'); // 'context', 'table-selection', 'domain-classification', 'classifying', 'generating', 'relationship-identification', 'graph'
  const [contextData, setContextData] = useState({ industry: '', businessFunctions: '', additionalContext: '' });
  const [selectedTables, setSelectedTables] = useState([]);
  const [domains, setDomains] = useState([]);
  const [showTableSelection, setShowTableSelection] = useState(false);
  const [showDomainClassification, setShowDomainClassification] = useState(false);
  const [showBrowseAll, setShowBrowseAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterConnection, setFilterConnection] = useState('all');
  const [manualDomains, setManualDomains] = useState([
    { id: 1, name: '', tables: [] },
    { id: 2, name: '', tables: [] },
    { id: 3, name: '', tables: [] }
  ]);
  
  // Auto-select all tables on initial load
  React.useEffect(() => {
    const allTables = connections.flatMap(conn =>
      conn.tables.map(table => ({
        ...table,
        connectionId: conn.id,
        connectionName: conn.name,
        connectionType: conn.type,
        connectionIcon: conn.icon
      }))
    );
    setSelectedTables(allTables);
  }, []);
  
  // Publish flow state
  const [publishState, setPublishState] = useState('idle'); // 'idle', 'input', 'progress', 'complete'
  const [publishConfig, setPublishConfig] = useState(null);
  
  // Auto-scroll when publish flow starts
  React.useEffect(() => {
    if (publishState === 'input' || publishState === 'progress') {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [publishState]);
  
  // NEW: Progressive classification state
  const [tablesToClassify, setTablesToClassify] = useState([]); // Array of {domain, table} pairs
  const [classifiedCount, setClassifiedCount] = useState(0); // How many have been classified so far
  const [isAutoDomainClassification, setIsAutoDomainClassification] = useState(false); // Track auto vs manual choice
  const [committedManualDomains, setCommittedManualDomains] = useState([]); // Only updates on confirm, not during typing
  
  // NEW: Data Product Builder state
  const [dataProductColumns, setDataProductColumns] = useState([]); // Array of selected column IDs for data product
  const [dataProductType, setDataProductType] = useState(''); // Type of data product (e.g., 'customer_360')
  const [dataProductStage, setDataProductStage] = useState(''); // Current stage: 'semantic-graph'
  const [showDataProductView, setShowDataProductView] = useState(false); // Toggle between graph and data product views
  const [graphViewMode, setGraphViewMode] = useState('shortlisted'); // 'shortlisted' or 'all' for graph view
  const [attributeConfigs, setAttributeConfigs] = useState({}); // Store alias, type overrides, aggregations per column
  const [showAttributeSearch, setShowAttributeSearch] = useState(false); // Toggle search popup for adding attributes
  const [attributeSearchQuery, setAttributeSearchQuery] = useState(''); // Search query for attributes
  const [attributeSearchType, setAttributeSearchType] = useState('all'); // 'dimension', 'measure', or 'all'
  const [activeConfigPopup, setActiveConfigPopup] = useState(null); // Active popup for attribute configuration
  const [tempConfigPopup, setTempConfigPopup] = useState(null); // Temporary config for popup (before Apply)
  const [dataProductResult, setDataProductResult] = useState(null); // Stores executed data product result
  const [rightPanelView, setRightPanelView] = useState('graph'); // 'graph', 'data', or 'publish' - right panel view toggle
  const [hasTriggeredPublish, setHasTriggeredPublish] = useState(false); // Track if publish has ever been triggered (keeps tabs visible)
  const [isExecutingQuery, setIsExecutingQuery] = useState(false); // Loading state for query execution
  const [attributeTab, setAttributeTab] = useState('dimensions'); // 'dimensions' or 'measures' tab
  const [editingAttributeId, setEditingAttributeId] = useState(null); // Track which attribute is being renamed
  const [filterPopupId, setFilterPopupId] = useState(null); // Track which dimension's filter popup is open
  const [filterSearchQuery, setFilterSearchQuery] = useState(''); // Search query within filter popup
  const attributeSearchRef = React.useRef(null);
  
  // Mock dimension values - in real app, this would come from backend
  const getDimensionValues = (colId, colName) => {
    const mockValues = {
      'Country': ['India', 'China', 'United States', 'Japan', 'Germany', 'United Kingdom', 'France', 'Brazil', 'Canada', 'Australia'],
      'Region': ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'],
      'Customer Name': ['Acme Corp', 'GlobalTech', 'MegaStore', 'DataFlow Inc', 'CloudFirst', 'TechGiant', 'InnovateCo', 'FutureLabs'],
      'Product Name': ['Widget Pro', 'Super Gadget', 'DataSync Tool', 'CloudManager', 'Analytics Suite', 'Integration Hub'],
      'Campaign Type': ['Email', 'Social Media', 'Display Ads', 'Search', 'Content Marketing', 'Webinar'],
      'Industry': ['Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 'Education'],
      'Status': ['Active', 'Inactive', 'Pending', 'Closed', 'On Hold'],
      'Opportunity Status': ['Open', 'Won', 'Lost', 'Negotiation', 'Qualified'],
    };
    // Return mock values if available, otherwise generate generic values
    if (mockValues[colName]) return mockValues[colName];
    // Generate generic values based on column name
    return ['Value 1', 'Value 2', 'Value 3', 'Value 4', 'Value 5', 'Value 6', 'Value 7', 'Value 8'];
  };
  
  // Close popups on Escape key or click outside
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setFilterPopupId(null);
        setActiveConfigPopup(null);
        setTempConfigPopup(null);
        setEditingAttributeId(null);
      }
    };
    
    const handleClickOutside = (e) => {
      // Check if click is inside popup or on trigger buttons
      const isInsideFilterPopup = e.target.closest('[data-popup="filter"]');
      const isInsideConfigPopup = e.target.closest('[data-popup="config"]');
      const isOnFilterTrigger = e.target.closest('[data-popup-trigger="filter"]');
      const isOnConfigTrigger = e.target.closest('[data-popup-trigger="config"]');
      
      // Only close filter popup if clicking outside it and not on its trigger
      if (!isInsideFilterPopup && !isOnFilterTrigger) {
        setFilterPopupId(null);
      }
      
      // Only close config popup if clicking outside it and not on its trigger
      if (!isInsideConfigPopup && !isOnConfigTrigger) {
        setActiveConfigPopup(null);
        setTempConfigPopup(null);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Open attribute search and scroll into view
  const openAttributeSearch = () => {
    setAttributeSearchType('all');
    setShowAttributeSearch(true);
    setTimeout(() => {
      attributeSearchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };
  
  // Generate mock data for data product
  const generateMockDataProductResult = (columns) => {
    const mockData = [];
    const allColumns = [...getCustomer360Columns(), ...getAvailableColumns()];
    const selectedCols = columns.map(colId => allColumns.find(c => c.id === colId)).filter(Boolean);
    
    for (let i = 0; i < 50; i++) {
      const row = {};
      selectedCols.forEach(col => {
        const config = attributeConfigs[col.id] || {};
        const effectiveType = config.type || col.type;
        
        if (effectiveType === 'dimension') {
          if (col.dataType === 'text') {
            if (col.name.includes('Name')) row[col.name] = `${col.name.split(' ')[0]} ${i + 1}`;
            else if (col.name.includes('Region')) row[col.name] = ['North', 'South', 'East', 'West'][i % 4];
            else if (col.name.includes('Type')) row[col.name] = ['Enterprise', 'SMB', 'Startup'][i % 3];
            else if (col.name.includes('Industry')) row[col.name] = ['Retail', 'Tech', 'Healthcare', 'Finance'][i % 4];
            else if (col.name.includes('Status')) row[col.name] = ['Active', 'Pending', 'Closed'][i % 3];
            else if (col.name.includes('Product')) row[col.name] = ['Product A', 'Product B', 'Product C'][i % 3];
            else row[col.name] = `${col.name} ${i + 1}`;
          } else if (col.dataType === 'date') {
            const date = new Date(2024, i % 12, (i % 28) + 1);
            row[col.name] = date.toISOString().split('T')[0];
          } else if (col.dataType === 'integer') {
            row[col.name] = 1000 + i;
          }
        } else {
          // Measure
          if (col.name.includes('Revenue') || col.name.includes('Value') || col.name.includes('Cost')) {
            row[col.name] = Math.round((Math.random() * 100000 + 10000) * 100) / 100;
          } else if (col.name.includes('Count') || col.name.includes('Employee')) {
            row[col.name] = Math.floor(Math.random() * 500) + 10;
          } else if (col.name.includes('Score') || col.name.includes('Rating') || col.name.includes('Index')) {
            row[col.name] = Math.round((Math.random() * 5 + 5) * 10) / 10;
          } else {
            row[col.name] = Math.round(Math.random() * 10000 * 100) / 100;
          }
        }
      });
      mockData.push(row);
    }
    
    return {
      columns: selectedCols,
      data: mockData
    };
  };
  
  // Execute query and generate data product
  const executeDataProductQuery = () => {
    setIsExecutingQuery(true);
    setIsProcessing(true);
    setProcessingStepText('Executing query...');
    
    // Simulate 5 second execution time
    setTimeout(() => {
      const result = generateMockDataProductResult(dataProductColumns);
      setDataProductResult(result);
      setRightPanelView('data');
      setIsExecutingQuery(false);
      setIsProcessing(false);
      setProcessingStepText('');
      
      // Add success message
      const successMsg = {
        id: messages.length + 1,
        role: 'assistant',
        content: `✓ Query executed successfully! Retrieved ${result.data.length} rows with ${result.columns.length} columns. View the data in the Data tab on the right panel.`,
        type: 'text'
      };
      setMessages(prev => [...prev, successMsg]);
    }, 5000);
  };
  
  const addDomain = () => {
    const newId = manualDomains.length + 1;
    setManualDomains([...manualDomains, { id: newId, name: '', tables: [] }]);
  };
  
  // Format number with K, M, B notation
  const formatCount = (num) => {
    if (!num) return '—';
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };
  
  // Generate SQL query for selected columns
  const generateSQLQuery = (selectedColumns, configs) => {
    // Group columns by table
    const tableColumns = {};
    selectedColumns.forEach(col => {
      const config = configs[col.id] || {};
      const effectiveType = config.type || col.type;
      const aggregation = config.aggregation || (effectiveType === 'measure' ? 'COUNT' : null);
      
      if (!tableColumns[col.table]) {
        tableColumns[col.table] = [];
      }
      tableColumns[col.table].push({
        ...col,
        effectiveType,
        aggregation
      });
    });
    
    const tables = Object.keys(tableColumns);
    const dimensions = selectedColumns.filter(col => {
      const config = configs[col.id] || {};
      return (config.type || col.type) === 'dimension';
    });
    const measures = selectedColumns.filter(col => {
      const config = configs[col.id] || {};
      return (config.type || col.type) === 'measure';
    });
    
    // Build SELECT clause
    let selectParts = [];
    
    // Add dimensions
    dimensions.forEach(col => {
      const tableAlias = col.table.replace(/\s+/g, '_').substring(0, 3).toLowerCase();
      selectParts.push(`    ${tableAlias}.${col.name.replace(/[\s\/\(\)\$]+/g, '_')}`);
    });
    
    // Add measures with aggregations
    measures.forEach(col => {
      const config = configs[col.id] || {};
      const agg = config.aggregation || 'COUNT';
      const tableAlias = col.table.replace(/\s+/g, '_').substring(0, 3).toLowerCase();
      const colName = col.name.replace(/[\s\/\(\)\$]+/g, '_');
      selectParts.push(`    ${agg}(${tableAlias}.${colName}) AS ${colName}`);
    });
    
    // Build FROM and JOIN clauses
    const mainTable = tables[0];
    const mainAlias = mainTable.replace(/\s+/g, '_').substring(0, 3).toLowerCase();
    
    let fromClause = `FROM ${mainTable.replace(/\s+/g, '_')} ${mainAlias}`;
    let joinClauses = [];
    
    tables.slice(1).forEach(table => {
      const alias = table.replace(/\s+/g, '_').substring(0, 3).toLowerCase();
      joinClauses.push(`LEFT JOIN ${table.replace(/\s+/g, '_')} ${alias} ON ${mainAlias}.C_ID = ${alias}.C_ID`);
    });
    
    // Build GROUP BY clause
    let groupByParts = dimensions.map(col => {
      const tableAlias = col.table.replace(/\s+/g, '_').substring(0, 3).toLowerCase();
      return `    ${tableAlias}.${col.name.replace(/[\s\/\(\)\$]+/g, '_')}`;
    });
    
    // Construct full query
    let sql = `SELECT\n${selectParts.join(',\n')}\n${fromClause}`;
    if (joinClauses.length > 0) {
      sql += `\n${joinClauses.join('\n')}`;
    }
    if (groupByParts.length > 0) {
      sql += `\nGROUP BY\n${groupByParts.join(',\n')}`;
    }
    sql += ';';
    
    return sql;
  };
  
  // State for SQL query expansion
  const [expandedSqlMessages, setExpandedSqlMessages] = useState({});
  const [streamedSqlMessages, setStreamedSqlMessages] = useState({}); // Track which messages have finished streaming
  const sqlMessageRefs = React.useRef({}); // Store refs for SQL messages
  
  // Callback for when SQL streaming completes
  const handleStreamComplete = React.useCallback((msgId) => {
    setStreamedSqlMessages(prev => ({ ...prev, [msgId]: true }));
  }, []);
  
  // Format SQL with syntax highlighting
  const formatSQL = React.useCallback((sql) => {
    const keywords = ['SELECT', 'FROM', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'JOIN', 'ON', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'AS', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'LIKE', 'BETWEEN', 'UNION', 'DISTINCT', 'LIMIT', 'OFFSET'];
    const functions = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'COALESCE', 'CAST', 'CONCAT', 'SUBSTRING', 'TRIM', 'UPPER', 'LOWER', 'DATE', 'YEAR', 'MONTH', 'DAY'];
    
    // Split into lines and process each
    const lines = sql.split('\n');
    const processedLines = lines.map(line => {
      let processedLine = line;
      
      // Highlight keywords (bright sky blue)
      keywords.forEach(kw => {
        const regex = new RegExp(`\\b(${kw})\\b`, 'gi');
        processedLine = processedLine.replace(regex, `<span class="text-sky-300 font-semibold">$1</span>`);
      });
      
      // Highlight functions (yellow)
      functions.forEach(fn => {
        const regex = new RegExp(`\\b(${fn})\\s*\\(`, 'gi');
        processedLine = processedLine.replace(regex, `<span class="text-amber-300">$1</span>(`);
      });
      
      // Highlight strings (orange)
      processedLine = processedLine.replace(/'([^']*)'/g, `<span class="text-orange-300">'$1'</span>`);
      
      // Highlight table aliases and column names after dots (pink/teal)
      processedLine = processedLine.replace(/(\w+)\.(\w+)/g, `<span class="text-pink-300">$1</span>.<span class="text-teal-300">$2</span>`);
      
      return processedLine;
    });
    
    return processedLines.join('\n');
  }, []);
  
  // Get sample columns for Customer 360 data product
  const getCustomer360Columns = () => {
    // Sample 15 dimensions and measures for Customer 360 with uniqueness and completeness
    const sampleColumns = [
      { id: 'col_1', name: 'Customer Name', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 98, completeness: 100, rowCount: 2341 },
      { id: 'col_2', name: 'C_ID', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'integer', uniqueness: 100, completeness: 100, rowCount: 2341 },
      { id: 'col_3', name: 'Industry / Vertical', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 45, completeness: 98, rowCount: 2341 },
      { id: 'col_4', name: 'Annual Revenue', table: 'Customer Demographics', domain: 'Customer', type: 'measure', dataType: 'float', uniqueness: 85, completeness: 95, rowCount: 2341 },
      { id: 'col_5', name: 'Region', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 12, completeness: 100, rowCount: 2341 },
      { id: 'col_6', name: 'Customer Type', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 8, completeness: 99, rowCount: 2341 },
      { id: 'col_7', name: 'Employee Count', table: 'Customer Demographics', domain: 'Customer', type: 'measure', dataType: 'integer', uniqueness: 72, completeness: 100, rowCount: 2341 },
      { id: 'col_8', name: 'Order Value ($)', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'float', uniqueness: 91, completeness: 98, rowCount: 8901 },
      { id: 'col_9', name: 'Order Date', table: 'Install Base', domain: 'Sales & Orders', type: 'dimension', dataType: 'date', uniqueness: 65, completeness: 100, rowCount: 8901 },
      { id: 'col_10', name: 'Product Name', table: 'Install Base', domain: 'Sales & Orders', type: 'dimension', dataType: 'text', uniqueness: 78, completeness: 100, rowCount: 8901 },
      { id: 'col_11', name: 'Opportunity Status', table: 'Opportunity', domain: 'Sales & Orders', type: 'dimension', dataType: 'text', uniqueness: 6, completeness: 97, rowCount: 3456 },
      { id: 'col_12', name: 'NPS_ID', table: 'NPS Survey', domain: 'Support & Services', type: 'dimension', dataType: 'integer', uniqueness: 100, completeness: 98, rowCount: 1524 },
      { id: 'col_13', name: 'Survey Score', table: 'NPS Survey', domain: 'Support & Services', type: 'measure', dataType: 'float', uniqueness: 35, completeness: 92, rowCount: 1524 },
      { id: 'col_14', name: 'Campaign Type', table: 'Campaigns', domain: 'Marketing', type: 'dimension', dataType: 'text', uniqueness: 15, completeness: 100, rowCount: 3456 },
      { id: 'col_15', name: 'Cost / Expense ($)', table: 'Campaigns', domain: 'Marketing', type: 'measure', dataType: 'float', uniqueness: 88, completeness: 95, rowCount: 3456 }
    ];
    
    return sampleColumns;
  };
  
  // Additional available columns for search/add functionality
  const getAvailableColumns = () => {
    return [
      { id: 'col_16', name: 'Contact Number', table: 'Customer Demographics', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 88, completeness: 100, rowCount: 2341 },
      { id: 'col_17', name: 'Account Manager', table: 'Customer Hierarchy', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 95, completeness: 100, rowCount: 1876 },
      { id: 'col_18', name: 'Party Name', table: 'Customer Hierarchy', domain: 'Customer', type: 'dimension', dataType: 'text', uniqueness: 92, completeness: 100, rowCount: 1876 },
      { id: 'col_19', name: 'CSAT Rating', table: 'Service Request', domain: 'Support & Services', type: 'measure', dataType: 'float', uniqueness: 78, completeness: 95, rowCount: 4521 },
      { id: 'col_20', name: 'Satisfaction Index', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'float', uniqueness: 88, completeness: 99, rowCount: 8901 },
      { id: 'col_21', name: 'Survey Date', table: 'NPS Survey', domain: 'Support & Services', type: 'dimension', dataType: 'date', uniqueness: 100, completeness: 100, rowCount: 1524 },
      { id: 'col_22', name: 'Survey Response', table: 'Delivery Survey', domain: 'Supply Chain', type: 'dimension', dataType: 'text', uniqueness: 85, completeness: 100, rowCount: 2890 },
      { id: 'col_23', name: 'Created Date', table: 'Opportunity', domain: 'Sales & Orders', type: 'dimension', dataType: 'date', uniqueness: 100, completeness: 98, rowCount: 3456 },
      { id: 'col_24', name: 'Close Date', table: 'Opportunity', domain: 'Sales & Orders', type: 'dimension', dataType: 'date', uniqueness: 95, completeness: 92, rowCount: 3456 },
      { id: 'col_25', name: 'Renewal Date', table: 'Renewals', domain: 'Sales & Orders', type: 'dimension', dataType: 'date', uniqueness: 100, completeness: 100, rowCount: 1234 },
      { id: 'col_26', name: 'Service Date', table: 'Service Request', domain: 'Support & Services', type: 'dimension', dataType: 'date', uniqueness: 98, completeness: 100, rowCount: 4521 },
      { id: 'col_27', name: 'Annual Cost', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'float', uniqueness: 88, completeness: 95, rowCount: 8901 },
      { id: 'col_28', name: 'Discount Amount', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'float', uniqueness: 76, completeness: 92, rowCount: 8901 },
      { id: 'col_29', name: 'Lifetime Value', table: 'Customer Demographics', domain: 'Customer', type: 'measure', dataType: 'float', uniqueness: 94, completeness: 89, rowCount: 2341 },
      { id: 'col_30', name: 'Churn Risk', table: 'Customer Demographics', domain: 'Customer', type: 'measure', dataType: 'float', uniqueness: 82, completeness: 100, rowCount: 2341 },
      { id: 'col_31', name: 'Product Family', table: 'Install Base', domain: 'Sales & Orders', type: 'dimension', dataType: 'text', uniqueness: 25, completeness: 100, rowCount: 8901 },
      { id: 'col_32', name: 'Product Category', table: 'Install Base', domain: 'Sales & Orders', type: 'dimension', dataType: 'text', uniqueness: 18, completeness: 100, rowCount: 8901 },
      { id: 'col_33', name: 'Product Line', table: 'Install Base', domain: 'Sales & Orders', type: 'dimension', dataType: 'text', uniqueness: 12, completeness: 98, rowCount: 8901 },
      { id: 'col_34', name: 'Quantity', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'integer', uniqueness: 45, completeness: 100, rowCount: 8901 },
      { id: 'col_35', name: 'Unit Price', table: 'Install Base', domain: 'Sales & Orders', type: 'measure', dataType: 'float', uniqueness: 72, completeness: 100, rowCount: 8901 }
    ];
  };
  
  // Track when classification completes to reheat simulation
  const [shouldReheatSimulation, setShouldReheatSimulation] = React.useState(false);
  const prevStepForReheat = React.useRef(currentStep);
  
  React.useEffect(() => {
    if (prevStepForReheat.current === 'classifying' && currentStep === 'generating') {
      setShouldReheatSimulation(true);
      setTimeout(() => setShouldReheatSimulation(false), 500);
    }
    prevStepForReheat.current = currentStep;
  }, [currentStep]);
  
  // Derive graph rendering step - group steps that show the same graph
  const graphRenderStep = useMemo(() => {
    if (currentStep === 'context' || currentStep === 'table-selection') {
      return 'empty';
    } else if (currentStep === 'table-selection-done' || currentStep === 'domain-classification' || currentStep === 'domain-manual' || currentStep === 'ready-to-generate') {
      return 'tables-only';
    } else if (currentStep === 'classifying' || currentStep === 'generating' || currentStep === 'relationship-identification' || currentStep === 'business-glossary' || currentStep === 'graph' || currentStep === 'completed' || currentStep === 'complete') {
      return 'full-hierarchy';
    }
    return currentStep;
  }, [currentStep]);
  
  // Generate semantic graph data dynamically based on current step
  // Use currentStep to ensure links are created when step changes to 'graph'
  // Use committedManualDomains instead of manualDomains to prevent re-renders during typing
  const { nodes: semanticNodes, links: semanticLinks } = useMemo(() => {
    return generateSemanticGraphData(currentStep, selectedTables, committedManualDomains, isAutoDomainClassification, classifiedCount);
  }, [currentStep, selectedTables, committedManualDomains, isAutoDomainClassification, classifiedCount]);
  
  // Memoize customer360 columns to prevent unnecessary recalculations
  const customer360ColumnsMemo = useMemo(() => getCustomer360Columns(), []);
  const availableColumnsMemo = useMemo(() => getAvailableColumns(), []);
  
  // Calculate highlighted nodes for data product builder
  const highlightedNodeIds = useMemo(() => {
    if (dataProductColumns.length === 0) return [];
    
    const highlighted = new Set();
    
    // Get column names and their table/domain mappings from selected IDs
    dataProductColumns.forEach(colId => {
      const colData = customer360ColumnsMemo.find(c => c.id === colId);
      if (!colData) return;
      
      // Find matching nodes in the semantic graph
      semanticNodes.forEach(node => {
        // Highlight the column node if name matches
        if (node.type === 'COLUMN' && node.name === colData.name) {
          highlighted.add(node.id);
          
          // Also highlight parent table
          if (node.parent_id) {
            highlighted.add(node.parent_id);
            
            // Find table's parent domain
            const tableNode = semanticNodes.find(n => n.id === node.parent_id);
            if (tableNode && tableNode.parent_id) {
              highlighted.add(tableNode.parent_id);
            }
          }
        }
      });
    });
    
    return Array.from(highlighted);
  }, [dataProductColumns, semanticNodes, customer360ColumnsMemo]);
  
  // Apply highlighting only when in shortlisted view mode - memoized
  const activeHighlightedNodeIds = useMemo(() => 
    graphViewMode === 'shortlisted' ? highlightedNodeIds : []
  , [graphViewMode, highlightedNodeIds]);
  
  // NEW: Progressive classification animation
  const classificationSummaryAddedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (currentStep === 'classifying') {
      // Reset the ref when starting classification
      classificationSummaryAddedRef.current = false;
      
      // Build the data structure to count tables
      const autoDataStructure = {
        "Marketing": { "Campaign Survey": [], "Campaigns": [], "Website": [] },
        "Customer": { "Customer Demographics": [], "Customer Hierarchy": [] },
        "Supply Chain": { "Delivery_Survey": [], "Inventory": [], "Logistics": [] },
        "Sales & Orders": { "Expense": [], "Install Base": [], "Opportunity": [], "Renewals": [] },
        "Support & Services": { "NPS Survey": [], "Prob_Statement_Issue": [], "Returns": [], "Service Request": [] },
        "Products": { "Product Engg": [], "Product Feature": [], "Product Hierarchy": [] }
      };
      
      let totalTables = 0;
      let domainCount = 0;
      if (isAutoDomainClassification) {
        totalTables = Object.values(autoDataStructure).reduce((acc, domain) => 
          acc + Object.keys(domain).length, 0
        );
        domainCount = Object.keys(autoDataStructure).length;
      } else {
        totalTables = committedManualDomains.reduce((acc, domain) => 
          acc + (domain.tables?.length || 0), 0
        );
        domainCount = committedManualDomains.filter(d => d.name && d.tables.length > 0).length;
      }
      
      // Start from 0 and increment
      setClassifiedCount(0);
      
      const interval = setInterval(() => {
        setClassifiedCount(prev => {
          const newCount = prev + 1;
          
          if (newCount >= totalTables) {
            clearInterval(interval);
            
            // Stop processing indicator and show summary (only once)
            setIsProcessing(false);
            setProcessingStepText('');
            
            // Update the existing domain-manual-entry message with classification results
            if (!classificationSummaryAddedRef.current) {
              classificationSummaryAddedRef.current = true;
              
              // Update domain-manual-entry with completed status and classification data
              setMessages(prev => prev.map(msg => 
                msg.type === 'domain-manual-entry' 
                  ? { 
                      ...msg, 
                      status: 'completed',
                      classificationData: { domainCount, tableCount: totalTables }
                    }
                  : msg
              ));
            }
            
            // When complete, transition to generating step
            setTimeout(() => {
              setCurrentStep('generating');
            }, 600);
            return totalTables;
          }
          return newCount;
        });
      }, 500); // 0.5 seconds per table
      
      return () => clearInterval(interval);
    }
  }, [currentStep, isAutoDomainClassification, committedManualDomains]);
  
  // Data profiling - show processing steps in typing indicator, only summaries in chat
  React.useEffect(() => {
    if (currentStep === 'generating') {
      // Calculate totals from semanticNodes
      const tableCount = semanticNodes.filter(n => n.type === 'TABLE').length;
      const columnCount = semanticNodes.filter(n => n.type === 'COLUMN').length;
      
      // Wait for previous step collapse animation before adding data profile message
      const addMsgTimer = setTimeout(() => {
        // Show processing indicator
        setIsProcessing(true);
        setProcessingStepText('');
        
        // Create data profile message with processing status
        const dimensionCount = Math.floor(columnCount * 0.65);
        const measureCount = columnCount - dimensionCount;
        const textCols = Math.floor(columnCount * 0.40);
        const intCols = Math.floor(columnCount * 0.25);
        const floatCols = Math.floor(columnCount * 0.15);
        const dateCols = Math.floor(columnCount * 0.10);
        const boolCols = Math.floor(columnCount * 0.05);
        const otherCols = columnCount - (textCols + intCols + floatCols + dateCols + boolCols);
        const avgCompleteness = (85 + Math.random() * 10).toFixed(1);
        const avgUniqueness = (70 + Math.random() * 20).toFixed(1);
        const healthRating = avgCompleteness > 90 ? 'Excellent' : avgCompleteness > 80 ? 'Good' : 'Fair';
        
        const dataProfileSummary = {
          id: messages.length + 101,
          role: 'assistant',
          type: 'data-profile-summary',
          status: 'processing',
          data: { 
            dimensionCount, 
            measureCount, 
            columnCount,
            textCols, 
            intCols, 
            floatCols, 
            dateCols, 
            boolCols, 
            otherCols,
            avgCompleteness, 
            avgUniqueness, 
            healthRating
          }
        };
        setMessages(prev => [...prev, dataProfileSummary]);
      }, 350);
      
      // Data profiling complete - update status and stop processing
      const completeTimer = setTimeout(() => {
        setMessages(prev => prev.map(msg => 
          msg.type === 'data-profile-summary' 
            ? { ...msg, status: 'completed' }
            : msg
        ));
        setIsProcessing(false);
        setProcessingStepText('');
        
        // Wait for collapse animation before transitioning to relationship identification
        setTimeout(() => {
          setCurrentStep('relationship-identification');
        }, 400);
      }, 7350);
      
      return () => {
        clearTimeout(addMsgTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [currentStep, semanticNodes]);
  
  // Relationship identification - show processing steps in typing indicator, only summaries in chat
  React.useEffect(() => {
    if (currentStep === 'relationship-identification') {
      // Wait for previous step collapse animation before showing this step
      const addMsgTimer = setTimeout(() => {
        // Show processing indicator
        setIsProcessing(true);
        setProcessingStepText('');
        
        // Create relationship message with processing status
        const relationshipMsg = {
          id: messages.length + 204,
          role: 'assistant',
          type: 'relationship-complete',
          status: 'processing',
          data: { relationshipCount: 10, pkCount: 20 }
        };
        setMessages(prev => [...prev, relationshipMsg]);
      }, 350);
      
      // Relationships complete - update status
      const completeTimer = setTimeout(() => {
        setMessages(prev => prev.map(msg => 
          msg.type === 'relationship-complete' 
            ? { ...msg, status: 'completed' }
            : msg
        ));
        setIsProcessing(false);
        setProcessingStepText('');
        
        // Wait for collapse animation before transitioning to business glossary step
        setTimeout(() => {
          setCurrentStep('business-glossary');
        }, 400);
      }, 12350);
      
      return () => {
        clearTimeout(addMsgTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [currentStep, semanticNodes]);
  
  // Business glossary - show processing steps
  React.useEffect(() => {
    if (currentStep === 'business-glossary') {
      // Wait for previous step collapse animation before adding glossary message
      const addMsgTimer = setTimeout(() => {
        // Calculate table and column counts for glossary
        const tableCount = semanticNodes.filter(n => n.type === 'TABLE').length;
        const columnCount = semanticNodes.filter(n => n.type === 'COLUMN').length;
        
        // Create glossary message with processing status
        const glossarySummaryMsg = {
          id: messages.length + 206,
          role: 'assistant',
          type: 'glossary-summary',
          status: 'processing',
          data: { tableCount, attributeCount: columnCount }
        };
        setMessages(prev => [...prev, glossarySummaryMsg]);
        
        // Show processing indicator for glossary generation
        setIsProcessing(true);
        setProcessingStepText('');
      }, 350);
      
      // After 5 seconds, mark business glossary as complete
      const completeTimer = setTimeout(() => {
        setMessages(prev => prev.map(msg => 
          msg.type === 'glossary-summary' 
            ? { ...msg, status: 'completed' }
            : msg
        ));
        setIsProcessing(false);
        setProcessingStepText('');
        
        // Wait for collapse animation before showing graph ready message
        setTimeout(() => {
          const graphReadyMsg = {
            id: messages.length + 207,
            role: 'assistant',
            type: 'graph-ready'
          };
          
          setMessages(prev => [...prev, graphReadyMsg]);
          
          // Mark as complete and show generate button
          setTimeout(() => {
            setCurrentStep('complete');
          }, 1000);
        }, 400);
      }, 5350);
      
      return () => {
        clearTimeout(addMsgTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [currentStep, semanticNodes]);
  
  // Handle resize
  const handleMouseDown = () => {
    setIsResizing(true);
  };
  
  const handleMouseMove = (e) => {
    if (isResizing) {
      const container = document.getElementById('main-container');
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
        if (newWidth >= 20 && newWidth <= 50) { // Min 20%, Max 50%
          setLeftWidth(newWidth);
        }
      }
    }
  };
  
  const handleMouseUp = () => {
    setIsResizing(false);
  };
  
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);
  
  // Get all available tables
  const getAllTables = () => {
    return connections.flatMap(conn =>
      conn.tables.map(table => ({
        ...table,
        connectionId: conn.id,
        connectionName: conn.name,
        connectionType: conn.type,
        connectionIcon: conn.icon
      }))
    );
  };
  
  // Get AI-suggested tables based on context (mock implementation)
  const getSuggestedTables = () => {
    const allTables = getAllTables();
    // Simple mock: suggest first 8 tables (in real implementation, this would use AI/ML)
    return allTables.slice(0, 8);
  };
  
  // Filter tables based on search and connection filter
  const getFilteredTables = () => {
    let filtered = getAllTables();
    
    // Apply connection filter
    if (filterConnection !== 'all') {
      filtered = filtered.filter(t => t.connectionId === filterConnection);
    }
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.connectionName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  };

  // Handle context form submission
  const handleContextSubmit = () => {
    if (!contextData.industry) {
      return; // Validation
    }
    
    // Show processing indicator (status will show "Processing" due to isProcessing being true)
    setIsProcessing(true);
    
    setTimeout(() => {
      // Now mark the context-form as completed
      setMessages(prev => prev.map(msg => 
        msg.type === 'context-form' 
          ? { ...msg, status: 'completed' }
          : msg
      ));
      
      setIsProcessing(false);
      
      // Wait for collapse animation to complete before showing next step
      setTimeout(() => {
        // Go to domain classification next - directly show manual entry
        const domainEntryMessage = {
          id: messages.length + 1,
          role: 'assistant',
          type: 'domain-manual-entry',
          introText: `Let's organize your ${selectedTables.length} tables into domains such as Customer, Products, Orders, Marketing, Sales, etc.\n\n💡 Tip: You don't need to classify all tables. Just add 2-3 sample tables to each domain - I'll automatically classify the rest based on your examples.`
        };
        
        setMessages(prev => [...prev, domainEntryMessage]);
        setCurrentStep('domain-manual');
      }, 500);
    }, 3000);
  };

  // Chat functions
  const sendMessage = () => {
    if (!inputMessage.trim()) return;
    
    const newMessage = {
      id: messages.length + 1,
      role: 'user',
      content: inputMessage,
      type: 'text'
    };
    
    setMessages([...messages, newMessage]);
    setInputMessage('');
    
    // Start processing indicator
    setIsProcessing(true);
    
    // Check if user is requesting a data product
    const lowerInput = inputMessage.toLowerCase();
    
    // Check for data analysis query - triggers QueryResponseUI with completeness workflow
    // Triggered by "customer 360" or related completeness queries
    const isDataAnalysisQuery = lowerInput.includes('customer 360') || 
                                lowerInput.includes('customer360') ||
                                (lowerInput.includes('completeness') && lowerInput.includes('attributes')) ||
                                (lowerInput.includes('low completeness') && lowerInput.includes('exclude'));
    
    // Check for Data Product builder - triggered by "product 360"
    const isDataProductRequest = lowerInput.includes('product 360') || 
                                  lowerInput.includes('product360') ||
                                  lowerInput.includes('build product');
    
    // Check if semantic graph is complete (handle both 'complete' and 'completed' states)
    const isGraphComplete = currentStep === 'business-glossary' || currentStep === 'graph' || currentStep === 'completed' || currentStep === 'complete';
    
    // Handle data analysis queries with the new QueryResponseUI
    if (isDataAnalysisQuery) {
      // Directly proceed without requiring graph completion
      // Switch to Data Analysis agent
      const dataAnalysisAgent = agents.dataDiscovery.find(a => a.id === 'data-analysis');
      if (dataAnalysisAgent) {
        setCurrentAgent(dataAnalysisAgent);
      }
      
      // Add the query response message - keep isProcessing true until QueryResponseUI completes via onComplete callback
      setTimeout(() => {
        const queryResponseMsg = {
          id: messages.length + 2,
          role: 'assistant',
          type: 'query-response',
          data: {
            userQuestion: inputMessage,
            timestamp: new Date().toISOString()
          }
        };
        setMessages(prev => [...prev, queryResponseMsg]);
      }, 500);
      return;
    }
    
    if (isDataProductRequest) {
      // Directly proceed with product 360 simulation without requiring graph completion
      // Switch to Data Products agent with processing delay
      simulateProcessing(() => {
        const dataProductsAgent = agents.dataApps.find(a => a.id === 'data-products');
        setCurrentAgent(dataProductsAgent);
        
        // Show breadcrumb by enabling data product view (but stay on semantic-graph stage initially)
        setShowDataProductView(true);
        setDataProductStage('semantic-graph'); // Start at semantic-graph in breadcrumb
        
        // Show processing indicator
        setIsProcessing(true);
        
        // Show response after brief delay
        setTimeout(() => {
            setIsProcessing(false);
            
            // First message: Agent switch confirmation
            const switchConfirmMsg = {
              id: messages.length + 2,
              role: 'assistant',
              content: '✓ Switched to Data Products Agent.',
              type: 'text'
            };
            
            // Second message: Introduction
            const introMsg = {
              id: messages.length + 3,
              role: 'assistant',
              content: 'Great! Let me help you build a Product 360 view. 📊\n\nI\'ve identified key dimensions and measures from your data. Review and adjust the selection below:',
              type: 'text'
            };
            
            // Get sample columns for product 360 (15 items)
            const customer360Columns = getCustomer360Columns();
            
            const dataProductMessage = {
              id: messages.length + 4,
              role: 'assistant',
              type: 'data-product-builder',
              data: {
                productType: 'product_360',
                columns: customer360Columns
              }
            };
            
            setMessages(prev => [...prev, switchConfirmMsg, introMsg, dataProductMessage]);
            
            // Auto-select all columns and highlight them in the graph
            const allColumnIds = customer360Columns.map(col => col.id);
            setDataProductColumns(allColumnIds);
            setDataProductType('customer_360');
          }, 3000);
      }, 3000);
      return;
    }
    
    if (dataProductType && dataProductColumns.length > 0) {
      // Handle attribute management commands when data product builder is active
      const handled = handleAttributeCommand(lowerInput, inputMessage);
      if (!handled) {
        // Check for data exploration queries
        const isExplorationQuery = detectExplorationQuery(lowerInput);
        if (isExplorationQuery) {
          handleExplorationQuery(inputMessage);
        } else {
          // Generic response for unrecognized commands
          setTimeout(() => {
            setIsProcessing(false);
            const helpMsg = {
              id: messages.length + 2,
              role: 'assistant',
              content: `I can help you manage attributes. Try commands like:\n• "Add [attribute name]"\n• "Remove [attribute name]"\n• "Change [attribute] to dimension/measure"\n• "Set [attribute] aggregation to SUM/AVG/COUNT/MIN/MAX"`,
              type: 'text'
            };
            setMessages(prev => [...prev, helpMsg]);
          }, 500);
        }
      }
    } else {
      // Check for data exploration queries even without data product
      const isExplorationQuery = detectExplorationQuery(lowerInput);
      if (isExplorationQuery) {
        handleExplorationQuery(inputMessage);
      } else {
        // For other messages, just stop processing after delay
        setTimeout(() => {
          setIsProcessing(false);
        }, 10000);
      }
    }
  };
  
  // Detect if a query is a data exploration query
  const detectExplorationQuery = (input) => {
    const explorationKeywords = [
      'show me', 'display', 'visualize', 'chart', 'graph', 'plot',
      'top', 'bottom', 'highest', 'lowest', 'compare', 'comparison',
      'across', 'by region', 'by customer', 'trend', 'distribution',
      'breakdown', 'analysis', 'analyze'
    ];
    return explorationKeywords.some(keyword => input.includes(keyword));
  };
  
  // Handle data analysis queries
  const handleExplorationQuery = (query) => {
    // Switch to Data Analysis agent
    const dataAnalysisAgent = agents.dataDiscovery.find(a => a.id === 'data-analysis');
    if (dataAnalysisAgent) {
      setCurrentAgent(dataAnalysisAgent);
    }
    
    setProcessingStepText('Analyzing your query...');
    
    // Create query-response message type to use QueryResponseUI component
    const queryResponseMsg = {
      id: messages.length + 2,
      role: 'assistant',
      type: 'query-response',
      data: {
        userQuestion: query,
        timestamp: new Date().toISOString()
      }
    };
    
    // Short delay then add the message - keep isProcessing true until QueryResponseUI completes
    setTimeout(() => {
      setProcessingStepText('');
      setMessages(prev => [...prev, queryResponseMsg]);
    }, 500);
  };
  
  // Handle attribute management commands from chat
  const handleAttributeCommand = (lowerInput, originalInput) => {
    const allColumns = [...getCustomer360Columns(), ...getAvailableColumns()];
    
    // Helper function for fuzzy matching - checks if all words in search term exist in attribute name
    const fuzzyMatch = (colName, searchTerm) => {
      const colWords = colName.toLowerCase().split(/[\s\/\-_]+/);
      const searchWords = searchTerm.toLowerCase().split(/[\s\/\-_]+/);
      
      // Check if all search words are found in column name words
      return searchWords.every(sw => 
        colWords.some(cw => cw.includes(sw) || sw.includes(cw))
      );
    };
    
    // Helper to find best match
    const findBestMatch = (searchTerm) => {
      // First try exact match
      let match = allColumns.find(col => col.name.toLowerCase() === searchTerm);
      if (match) return match;
      
      // Then try fuzzy match
      match = allColumns.find(col => fuzzyMatch(col.name, searchTerm));
      if (match) return match;
      
      // Then try partial match
      match = allColumns.find(col => 
        col.name.toLowerCase().includes(searchTerm) ||
        searchTerm.includes(col.name.toLowerCase())
      );
      return match;
    };
    
    // Helper to find similar attributes for suggestions
    const findSimilarAttributes = (searchTerm) => {
      const searchWords = searchTerm.toLowerCase().split(/[\s\/\-_]+/);
      return allColumns
        .filter(col => {
          const colWords = col.name.toLowerCase().split(/[\s\/\-_]+/);
          return searchWords.some(sw => colWords.some(cw => cw.includes(sw) || sw.includes(cw)));
        })
        .slice(0, 3)
        .map(col => col.name);
    };
    
    // Add attribute command
    const addMatch = lowerInput.match(/^(?:add|include|select)\s+(.+)$/i);
    if (addMatch) {
      const searchTerm = addMatch[1].trim().toLowerCase();
      const matchedCol = findBestMatch(searchTerm);
      
      if (matchedCol) {
        if (dataProductColumns.includes(matchedCol.id)) {
          setTimeout(() => {
            setIsProcessing(false);
            const alreadyMsg = {
              id: messages.length + 2,
              role: 'assistant',
              content: `"${matchedCol.name}" is already in your selection.`,
              type: 'text'
            };
            setMessages(prev => [...prev, alreadyMsg]);
          }, 300);
        } else {
          setDataProductColumns(prev => [...prev, matchedCol.id]);
          setTimeout(() => {
            setIsProcessing(false);
            const addedMsg = {
              id: messages.length + 2,
              role: 'assistant',
              content: `✓ Added "${matchedCol.name}" (${matchedCol.type}) from ${matchedCol.table}.`,
              type: 'text'
            };
            setMessages(prev => [...prev, addedMsg]);
          }, 300);
        }
        return true;
      } else {
        const similar = findSimilarAttributes(searchTerm);
        setTimeout(() => {
          setIsProcessing(false);
          const notFoundMsg = {
            id: messages.length + 2,
            role: 'assistant',
            content: similar.length > 0 
              ? `Couldn't find "${addMatch[1]}". Did you mean: ${similar.join(', ')}?`
              : `Couldn't find an attribute matching "${addMatch[1]}". Use the + Add button to browse available attributes.`,
            type: 'text'
          };
          setMessages(prev => [...prev, notFoundMsg]);
        }, 300);
        return true;
      }
    }
    
    // Remove attribute command
    const removeMatch = lowerInput.match(/^(?:remove|delete|exclude|deselect)\s+(.+)$/i);
    if (removeMatch) {
      const searchTerm = removeMatch[1].trim().toLowerCase();
      const matchedCol = findBestMatch(searchTerm);
      
      if (matchedCol && dataProductColumns.includes(matchedCol.id)) {
        setDataProductColumns(prev => prev.filter(id => id !== matchedCol.id));
        setTimeout(() => {
          setIsProcessing(false);
          const removedMsg = {
            id: messages.length + 2,
            role: 'assistant',
            content: `✓ Removed "${matchedCol.name}" from selection.`,
            type: 'text'
          };
          setMessages(prev => [...prev, removedMsg]);
        }, 300);
        return true;
      } else if (matchedCol) {
        setTimeout(() => {
          setIsProcessing(false);
          const notSelectedMsg = {
            id: messages.length + 2,
            role: 'assistant',
            content: `"${matchedCol.name}" is not in your current selection.`,
            type: 'text'
          };
          setMessages(prev => [...prev, notSelectedMsg]);
        }, 300);
        return true;
      }
    }
    
    // Change type command
    const changeTypeMatch = lowerInput.match(/^(?:change|convert|make|set)\s+(.+?)\s+(?:to|as)\s+(dimension|measure)$/i);
    if (changeTypeMatch) {
      const searchTerm = changeTypeMatch[1].trim().toLowerCase();
      const newType = changeTypeMatch[2].toLowerCase();
      const matchedCol = findBestMatch(searchTerm);
      
      if (matchedCol) {
        setAttributeConfigs(prev => ({
          ...prev,
          [matchedCol.id]: {
            ...prev[matchedCol.id],
            type: newType,
            aggregation: newType === 'measure' ? (prev[matchedCol.id]?.aggregation || 'COUNT') : null
          }
        }));
        setTimeout(() => {
          setIsProcessing(false);
          const changedMsg = {
            id: messages.length + 2,
            role: 'assistant',
            content: `✓ Changed "${matchedCol.name}" to ${newType}${newType === 'measure' ? ' (SUM)' : ''}.`,
            type: 'text'
          };
          setMessages(prev => [...prev, changedMsg]);
        }, 300);
        return true;
      }
    }
    
    // Set aggregation command
    const aggMatch = lowerInput.match(/^(?:set|change)\s+(.+?)\s+(?:aggregation|agg)\s+(?:to\s+)?(sum|avg|count|min|max)$/i);
    if (aggMatch) {
      const searchTerm = aggMatch[1].trim().toLowerCase();
      const newAgg = aggMatch[2].toUpperCase();
      const matchedCol = findBestMatch(searchTerm);
      
      if (matchedCol) {
        setAttributeConfigs(prev => ({
          ...prev,
          [matchedCol.id]: {
            ...prev[matchedCol.id],
            type: 'measure',
            aggregation: newAgg
          }
        }));
        setTimeout(() => {
          setIsProcessing(false);
          const aggMsg = {
            id: messages.length + 2,
            role: 'assistant',
            content: `✓ Set "${matchedCol.name}" aggregation to ${newAgg}.`,
            type: 'text'
          };
          setMessages(prev => [...prev, aggMsg]);
        }, 300);
        return true;
      }
    }
    
    return false;
  };
  
  const handleTableSelection = (table) => {
    const isSelected = selectedTables.some(t => t.id === table.id);
    if (isSelected) {
      setSelectedTables(selectedTables.filter(t => t.id !== table.id));
    } else {
      setSelectedTables([...selectedTables, table]);
    }
  };
  
  const handleSelectAllTables = () => {
    const allTables = getAllTables();
    if (selectedTables.length === allTables.length) {
      // Deselect all
      setSelectedTables([]);
    } else {
      // Select all
      setSelectedTables(allTables);
    }
  };
  
  const confirmTableSelection = () => {
    // Update the table-selection-browse message status
    setMessages(prev => prev.map(msg => 
      msg.type === 'table-selection-browse' 
        ? { ...msg, status: 'completed' }
        : msg
    ));
    
    setShowTableSelection(false);
    setCurrentStep('table-selection-done');
    
    // Show processing indicator
    setIsProcessing(true);
    
    // Trigger next bot response - Business Context form
    setTimeout(() => {
      setIsProcessing(false);
      
      const contextFormMessage = {
        id: messages.length + 1,
        role: 'assistant',
        type: 'context-form'
      };
      
      setMessages(prev => [...prev, contextFormMessage]);
      setCurrentStep('context');
    }, 2000);
  };

  
  // Handle toggling column selection for data product builder
  const handleDataProductColumnToggle = (columnId) => {
    setDataProductColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };
  
  // Update attribute configuration (alias, type, aggregation)
  const updateAttributeConfig = (columnId, field, value) => {
    setAttributeConfigs(prev => ({
      ...prev,
      [columnId]: {
        ...prev[columnId],
        [field]: value
      }
    }));
  };
  
  // Get effective attribute config (with defaults from original column data)
  const getAttributeConfig = (column) => {
    const config = attributeConfigs[column.id] || {};
    return {
      alias: config.alias || column.name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
      type: config.type || column.type,
      aggregation: config.aggregation || (column.type === 'measure' ? 'COUNT' : null)
    };
  };
  
  // Remove attribute from selection
  const removeAttribute = (columnId) => {
    setDataProductColumns(prev => prev.filter(id => id !== columnId));
    // Also remove its config
    setAttributeConfigs(prev => {
      const newConfigs = { ...prev };
      delete newConfigs[columnId];
      return newConfigs;
    });
  };
  
  // Add attributes from search results
  const addAttributesFromSearch = (columnIds) => {
    setDataProductColumns(prev => {
      const newIds = columnIds.filter(id => !prev.includes(id));
      return [...prev, ...newIds];
    });
    setShowAttributeSearch(false);
    setAttributeSearchQuery('');
  };
  
  // Get all columns (initial + available) for searching
  const getAllColumnsForSearch = () => {
    const initial = getCustomer360Columns();
    const available = getAvailableColumns();
    return [...initial, ...available];
  };
  
  // Filter columns based on search query
  const getFilteredSearchResults = () => {
    const allColumns = getAllColumnsForSearch();
    const query = attributeSearchQuery.toLowerCase();
    
    return allColumns.filter(col => {
      // Exclude already selected
      if (dataProductColumns.includes(col.id)) return false;
      
      // Filter by type if specified
      if (attributeSearchType !== 'all' && col.type !== attributeSearchType) return false;
      
      // Filter by search query
      if (query) {
        return (
          col.name.toLowerCase().includes(query) ||
          col.table.toLowerCase().includes(query) ||
          col.domain.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  };

  const handleDomainChoice = (choice) => {
    if (choice === 'auto') {
      // Keep domain-options as 'active' until classification completes
      // Status will be set to 'completed' when domain-classification-summary is added
      
      // Show processing indicator with classification step text
      setIsProcessing(true);
      setProcessingStepText('Classifying tables into domains...');
      
      // Set auto classification flag
      setIsAutoDomainClassification(true);
      
      // Start progressive classification
      setCurrentStep('classifying');
    } else {
      // Manual domain classification
      // Update the domain-options message status
      setMessages(prev => prev.map(msg => 
        msg.type === 'domain-options' 
          ? { ...msg, status: 'completed' }
          : msg
      ));
      
      // Show processing indicator
      setIsProcessing(true);
      
      // Set manual classification flag
      setIsAutoDomainClassification(false);
      
      setTimeout(() => {
        setIsProcessing(false);
        
        const domainEntryMessage = {
          id: messages.length + 1,
          role: 'assistant',
          type: 'domain-manual-entry',
          introText: `Great! Let's organize your ${selectedTables.length} tables into domains.\n\n💡 Tip: You don't need to classify all tables. Just add 2-3 sample tables to each domain - I'll automatically classify the rest based on your examples.`
        };
        
        setMessages(prev => [...prev, domainEntryMessage]);
        setCurrentStep('domain-manual');
      }, 1000);
    }
  };
  
  const handleManualDomainChange = (domainId, field, value) => {
    setManualDomains(prev => prev.map(domain => 
      domain.id === domainId ? { ...domain, [field]: value } : domain
    ));
  };
  
  const handleDomainTableSelection = (domainId, tableId) => {
    setManualDomains(prev => prev.map(domain => {
      if (domain.id === domainId) {
        const isSelected = domain.tables.includes(tableId);
        return {
          ...domain,
          tables: isSelected 
            ? domain.tables.filter(id => id !== tableId)
            : [...domain.tables, tableId]
        };
      }
      return domain;
    }));
  };
  
  const confirmDomainClassification = () => {
    // For demo purposes: Populate manualDomains with the original classification
    // Build domains from autoDataStructure
    const autoDataStructure = {
      "Marketing": ["Campaign Survey", "Campaigns", "Website"],
      "Customer": ["Customer Demographics", "Customer Hierarchy"],
      "Supply Chain": ["Delivery_Survey", "Inventory", "Logistics"],
      "Sales & Orders": ["Expense", "Install Base", "Opportunity", "Renewals"],
      "Support & Services": ["NPS Survey", "Prob_Statement_Issue", "Returns", "Service Request"],
      "Products": ["Product Engg", "Product Feature", "Product Hierarchy"]
    };
    
    // Map domain names to table IDs from selectedTables
    const populatedDomains = Object.entries(autoDataStructure).map(([domainName, tableNames], index) => {
      const tableIds = tableNames
        .map(tableName => {
          const table = selectedTables.find(t => t.name === tableName);
          return table ? table.id : null;
        })
        .filter(id => id !== null);
      
      return {
        id: index + 1,
        name: domainName,
        tables: tableIds
      };
    });
    
    // Update manualDomains with the populated data
    setManualDomains(populatedDomains);
    
    // Commit the domains (this triggers graph update)
    setCommittedManualDomains(populatedDomains);
    
    // Don't mark as completed yet - let it show "Processing" during classifying step
    // Status will be set to 'completed' when classification animation finishes
    
    // Use auto classification for the graph
    setIsAutoDomainClassification(true);
    
    // Show processing indicator with classification step text
    setIsProcessing(true);
    setProcessingStepText('Classifying tables into domains...');
    
    // Start progressive classification (same as auto)
    setCurrentStep('classifying');
  };
  
  const skipDomainClassification = () => {
    // Don't mark as completed yet - let it show "Processing" during classifying step
    
    // Use auto classification (will use default domains from mock data)
    setIsAutoDomainClassification(true);
    
    // Show processing indicator with classification step text
    setIsProcessing(true);
    setProcessingStepText('Auto-classifying tables into domains...');
    
    // Start progressive classification
    setCurrentStep('classifying');
  };
  
  const startGeneration = () => {
    setCurrentStep('generating');
    setShowTableSelection(false);
    
    // Show processing indicator
    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      const generatingMessage = {
        id: messages.length + 1,
        role: 'assistant',
        content: `🚀 Starting semantic graph generation...\n\nAnalyzing ${selectedTables.length} tables and discovering relationships...`,
        type: 'text'
      };
      
      setMessages(prev => [...prev, generatingMessage]);
    }, 3500);
  };

  // B2B SaaS Mock Connections - From Excel Data
  const connections = [
    {
      id: 'conn-1',
      name: 'B2B SaaS Data Platform',
      type: 'Snowflake',
      icon: 'https://companieslogo.com/img/orig/SNOW-35164165.png',
      status: 'connected',
      tables: [
        // Marketing Tables
        { id: 'table-1', name: 'Campaign Survey', rows: 1524, columns: 9, schema: 'marketing' },
        { id: 'table-2', name: 'Campaigns', rows: 3456, columns: 16, schema: 'marketing' },
        { id: 'table-3', name: 'Website', rows: 8901, columns: 9, schema: 'marketing' },
        
        // Customer Tables
        { id: 'table-4', name: 'Customer Demographics', rows: 2341, columns: 9, schema: 'customer' },
        { id: 'table-5', name: 'Customer Hierarchy', rows: 1876, columns: 8, schema: 'customer' },
        
        // Supply Chain Tables
        { id: 'table-6', name: 'Delivery_Survey', rows: 4532, columns: 8, schema: 'supply_chain' },
        { id: 'table-7', name: 'Inventory', rows: 6789, columns: 11, schema: 'supply_chain' },
        { id: 'table-8', name: 'Logistics', rows: 5432, columns: 10, schema: 'supply_chain' },
        
        // Sales & Orders Tables
        { id: 'table-9', name: 'Expense', rows: 9876, columns: 11, schema: 'sales' },
        { id: 'table-10', name: 'Install Base', rows: 12345, columns: 14, schema: 'sales' },
        { id: 'table-11', name: 'Opportunity', rows: 7654, columns: 13, schema: 'sales' },
        { id: 'table-12', name: 'Renewals', rows: 3210, columns: 7, schema: 'sales' },
        
        // Support & Services Tables
        { id: 'table-13', name: 'NPS Survey', rows: 5678, columns: 9, schema: 'support' },
        { id: 'table-14', name: 'Prob_Statement_Issue', rows: 4321, columns: 8, schema: 'support' },
        { id: 'table-15', name: 'Returns', rows: 2987, columns: 9, schema: 'support' },
        { id: 'table-16', name: 'Service Request', rows: 8765, columns: 11, schema: 'support' },
        
        // Products Tables
        { id: 'table-17', name: 'Product Engg', rows: 1234, columns: 10, schema: 'products' },
        { id: 'table-18', name: 'Product Feature', rows: 987, columns: 8, schema: 'products' },
        { id: 'table-19', name: 'Product Hierarchy', rows: 2109, columns: 7, schema: 'products' }
      ]
    }
  ];

  const toggleConnection = (connectionId) => {
    setExpandedConnections(prev => ({
      ...prev,
      [connectionId]: !prev[connectionId]
    }));
  };

  const handleSelectTable = (connection, table) => {
    setSelectedItem({ connection, table });
  };

  const handleSelectConnection = (connection) => {
    setSelectedItem({ connection, table: null });
  };

  return (
    <div className="h-full relative overflow-hidden bg-slate-950">
      <ToastContainer />
      
      <div className="relative z-10 h-full">
        {/* Floating Connections Panel - Slides in from right */}
        <div 
          className={`fixed top-0 right-0 h-full w-96 bg-slate-900/95 backdrop-blur-lg shadow-2xl z-50 transition-transform duration-300 ease-in-out overflow-y-auto ${
            showConnectionsPanel ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Panel Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur-lg p-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white font-heading">Data Connections</h2>
              <p className="text-xs text-gray-500 mt-1">
                {connections.length} connections • {connections.reduce((acc, conn) => acc + conn.tables.length, 0)} tables
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowConnectionsPanel(false)}
              className="text-gray-400 hover:text-white"
            >
              <Database className="w-5 h-5" />
            </Button>
          </div>

          {/* Connections List - Same as sidebar */}
          <aside className="overflow-y-auto">
          <div className="p-4">
            <div className="space-y-2">
              {connections.map((connection) => (
                <div key={connection.id}>
                  <div
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedItem?.connection?.id === connection.id && !selectedItem?.table
                        ? 'bg-blue-600/20'
                        : 'hover:bg-white/[0.03]'
                    }`}
                    onClick={() => {
                      toggleConnection(connection.id);
                      handleSelectConnection(connection);
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleConnection(connection.id);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        {expandedConnections[connection.id] ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <img
                        src={connection.icon}
                        alt={connection.type}
                        className="w-6 h-6 object-contain"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {connection.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {connection.tables.length} tables
                        </p>
                      </div>
                    </div>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>

                  {/* Tables List */}
                  {expandedConnections[connection.id] && (
                    <div className="ml-6 mt-1 space-y-1">
                      {connection.tables.map((table) => (
                        <div
                          key={table.id}
                          onClick={() => handleSelectTable(connection, table)}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            selectedItem?.table?.id === table.id
                              ? 'bg-blue-600/20'
                              : 'hover:bg-white/[0.03]'
                          }`}
                        >
                          <Table className="w-4 h-4 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{table.name}</p>
                            <p className="text-xs text-gray-500">{table.columns} cols • {table.rows.toLocaleString()} rows</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>
        </div>

        {/* Main Content Area - Resizable Layout */}
        <main id="main-container" className="w-full h-full flex overflow-hidden">
          {/* Collapsed Left Panel Restore Strip */}
          {collapsedPanel === 'left' && (
            <div 
              className="h-full w-10 bg-slate-900 flex flex-col items-center justify-center cursor-pointer transition-colors group relative"
              onClick={collapseLeft}
              title="Expand Chat"
            >
              {/* Hover line - same as divider */}
              <div className="absolute right-0 top-0 w-[1px] h-full bg-slate-700/30 group-hover:bg-blue-500/60 transition-colors" />
              <div className="flex flex-col items-center gap-2">
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-blue-400 transition-colors" />
                <span className="text-[10px] text-gray-500 group-hover:text-blue-400 transition-colors" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Chat</span>
              </div>
            </div>
          )}
          
          {/* Left: Conversational Bot with Optional Session Sidebar */}
          {collapsedPanel !== 'left' && (
          <div 
            className="flex flex-col bg-slate-900"
            style={{ width: collapsedPanel === 'right' ? '100%' : `${leftWidth}%` }}
          >
            {/* OPTION 2: Header-Based Session Management (only for Insights Generator) */}
            {currentAgent.id === 'insights-generator' && (
              <div className="flex flex-col h-full">
                {/* Chat Header with Session Controls */}
                <div className="border-b border-slate-700/40 bg-slate-900/60 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                        <span className="text-lg">{currentAgent.icon}</span>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white font-heading">{getAgentTitle()}</h2>
                        <p className="text-xs text-gray-400">Header-based session management</p>
                      </div>
                    </div>
                    
                    {/* Session Controls in Header */}
                    <div className="flex items-center gap-2">
                      {/* New Session Button */}
                      <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center gap-1 transition-colors">
                        <Plus className="w-3 h-3" />
                        New
                      </button>
                      
                      {/* Current Session Dropdown */}
                      <div className="relative" ref={agentDropdownRef}>
                        <button 
                          onClick={() => setShowSessionSidebar(!showSessionSidebar)}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-md flex items-center gap-2 min-w-40 transition-colors"
                        >
                          <span className="truncate">{currentSession?.name}</span>
                          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showSessionSidebar ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Session Dropdown */}
                        {showSessionSidebar && (
                          <div className="absolute top-full right-0 mt-1 w-72 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                            <div className="p-2">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-2 py-1">Recent Sessions</p>
                              {sessions.map((session) => (
                                <div 
                                  key={session.id} 
                                  onClick={() => {
                                    setCurrentSession(session);
                                    setShowSessionSidebar(false);
                                  }}
                                  className={`p-2 hover:bg-slate-800 rounded-md cursor-pointer group transition-colors ${
                                    currentSession?.id === session.id ? 'bg-slate-800 border border-blue-500/30' : ''
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-white truncate">{session.name}</p>
                                      <p className="text-[10px] text-gray-400">15 messages • 2 hours ago</p>
                                    </div>
                                    <div className="flex opacity-0 group-hover:opacity-100 transition-opacity gap-1 ml-2">
                                      <button 
                                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Edit session name functionality
                                        }}
                                      >
                                        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                      </button>
                                      <button 
                                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Delete session functionality
                                        }}
                                      >
                                        <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Separator */}
                            <div className="border-t border-slate-700/50 mx-2"></div>
                            
                            {/* Additional Actions */}
                            <div className="p-2">
                              <button className="w-full p-2 text-xs text-gray-400 hover:text-white hover:bg-slate-800 rounded-md transition-colors text-left">
                                Clear all sessions
                              </button>
                              <button className="w-full p-2 text-xs text-gray-400 hover:text-white hover:bg-slate-800 rounded-md transition-colors text-left">
                                Export sessions
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Demo Info */}
                      <div className="text-[10px] text-gray-400 bg-slate-800/50 px-2 py-1 rounded">
                        Option 2
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Chat Messages */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Demo Message */}
                  <div className="bg-slate-800/40 rounded-lg p-4">
                    <div className="text-center text-gray-300">
                      <h3 className="font-semibold mb-2">📋 Option 2: Header-Based Session Management</h3>
                      <p className="text-sm mb-3">Sessions are managed through header controls - compact and accessible!</p>
                      <div className="text-xs text-gray-400 space-y-1">
                        <p>• Current session name shown in header dropdown</p>
                        <p>• Click &quot;New&quot; button to create new sessions</p>
                        <p>• Click session name dropdown to switch sessions</p>
                        <p>• Hover over sessions for edit/delete options</p>
                        <p>• All session management in one place</p>
                      </div>
                      <div className="mt-4 pt-3 space-y-1">
                        <p className="text-xs text-blue-400">✅ Pros: Compact design, quick access, current session visible</p>
                        <p className="text-xs text-yellow-400">⚠️ Cons: Dropdown can get long with many sessions</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center text-gray-500 text-sm">
                    Current Session: <span className="text-blue-400">{currentSession?.name}</span>
                  </div>
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Input Area */}
                <div className="bg-slate-900/40">
                  <div className="p-4">
                    <div className="bg-slate-800/60 rounded-xl overflow-hidden">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Type a message to test header-based session functionality..."
                        className="w-full bg-transparent border-0 text-white placeholder:text-gray-500 resize-none min-h-[60px] p-4 pb-2 text-sm focus:ring-0 focus:outline-none"
                        style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
                        rows={1}
                      />
                      <div className="flex items-center justify-between px-4 pb-3">
                        <div className="flex items-center gap-3">
                          <button className="w-7 h-7 rounded-full bg-slate-700/60 hover:bg-slate-700 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-all duration-200">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button 
                          disabled={isProcessing}
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                            isProcessing 
                              ? 'bg-blue-500/20 text-blue-400 cursor-not-allowed' 
                              : 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed'
                          }`}
                        >
                          {isProcessing ? (
                            <Square className="w-3 h-3 fill-current" />
                          ) : (
                            <ArrowUp className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            
            {/* Regular Chat Layout (for non-insights agents) */}
            {currentAgent.id !== 'insights-generator' && (
              <>
                {/* Chat Messages */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((message, index) => {
                    // Check if we need a separator (user message following a non-user message)
                    const prevMessage = index > 0 ? messages[index - 1] : null;
                    const showSeparator = message.role === 'user' && prevMessage && prevMessage.role !== 'user';
                    
                    if (message.type === 'context-form') {
                      return (
                        <StepCard
                          key={message.id}
                          title="Business Context"
                          status={message.status === 'completed' ? 'completed' : (isProcessing && currentStep === 'context' ? 'active' : 'pending')}
                          defaultExpanded={message.status !== 'completed'}
                          autoCollapseOnComplete={true}
                          summary="Industry and business functions defined"
                          duration="1.2s"
                          statusText="Collecting context"
                        >
                          <div className="space-y-4">
                            <p className="text-sm text-gray-400">Help me understand your data better:</p>
                            
                            <div className="space-y-3">
                              <div className="space-y-2">
                                <Label className="text-gray-300 text-sm">Industry Domain *</Label>
                                <Select 
                                  value={contextData.industry} 
                                  onValueChange={(value) => setContextData({ ...contextData, industry: value })}
                                >
                                  <SelectTrigger className="text-white">
                                    <SelectValue placeholder="Select your industry" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {industries.map((industry) => (
                                      <SelectItem 
                                        key={industry} 
                                        value={industry}
                                      >
                                        {industry}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="space-y-2">
                                <Label className="text-gray-300 text-sm">Business Functions (Optional)</Label>
                                <Input
                                  value={contextData.businessFunctions}
                                  onChange={(e) => setContextData({ ...contextData, businessFunctions: e.target.value })}
                                  placeholder="e.g., Sales, Marketing, Operations"
                                  className="text-white"
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label className="text-gray-300 text-sm">Additional Context (Optional)</Label>
                                <Textarea
                                  value={contextData.additionalContext}
                                  onChange={(e) => setContextData({ ...contextData, additionalContext: e.target.value })}
                                  placeholder="Any other relevant information about your data..."
                                  className="text-white min-h-[80px] resize-none"
                                />
                              </div>
                              
                              <Button
                                onClick={handleContextSubmit}
                                disabled={!contextData.industry}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                              >
                                Continue
                              </Button>
                            </div>
                          </div>
                        </StepCard>
                      );
                    }
                    
                    if (message.type === 'table-selection-browse') {
                      return (
                        <StepCard
                          key={message.id}
                          title="Table Selection"
                          status={message.status === 'completed' ? 'completed' : (isProcessing && currentStep === 'table-selection' ? 'active' : 'pending')}
                          defaultExpanded={false}
                          autoCollapseOnComplete={true}
                          summary={`${selectedTables.length} tables selected`}
                          duration="2.8s"
                          statusText="Selecting tables"
                        >
                          <div className="space-y-3">
                            {message.introText && (
                              <p className="text-sm text-gray-400">{message.introText}</p>
                            )}
                            
                            <div className="flex gap-2">
                              <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search tables..."
                                className="flex-1 text-white"
                              />
                              <Select value={filterConnection} onValueChange={setFilterConnection}>
                                <SelectTrigger className="w-48 text-white">
                                  <SelectValue placeholder="Filter by connection" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Connections</SelectItem>
                                  {connections.map((conn) => (
                                    <SelectItem key={conn.id} value={conn.id}>
                                      {conn.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <p className="text-sm text-gray-400">
                                Showing {getFilteredTables().length} of {message.totalTables} tables
                              </p>
                              <Button
                                size="sm"
                                onClick={handleSelectAllTables}
                                variant="outline"
                                className={`text-gray-300 hover:bg-slate-700 ${
                                  selectedTables.length === getAllTables().length && selectedTables.length > 0
                                    ? 'border-blue-500 text-blue-400'
                                    : ''
                                }`}
                              >
                                {selectedTables.length === getAllTables().length && selectedTables.length > 0
                                  ? 'Deselect All'
                                  : 'Select All'}
                              </Button>
                            </div>
                            
                            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                              {getFilteredTables().map((table) => {
                                const isSelected = selectedTables.some(t => t.id === table.id);
                                return (
                                  <div
                                    key={table.id}
                                    onClick={() => handleTableSelection(table)}
                                    className={`p-2.5 rounded-lg cursor-pointer transition-all duration-200 ${
                                      isSelected
                                        ? 'bg-blue-500/15'
                                        : 'bg-slate-900/40 hover:bg-slate-900/60'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-7 h-7 rounded flex items-center justify-center ${
                                        isSelected ? 'bg-blue-500/20' : 'bg-slate-700/50'
                                      }`}>
                                        <Table className={`w-3.5 h-3.5 ${isSelected ? 'text-blue-400' : 'text-gray-400'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-gray-300'}`}>{table.name}</p>
                                        <p className="text-xs text-gray-500 truncate">{table.connectionName}</p>
                                      </div>
                                      <p className="text-xs text-gray-500 flex-shrink-0">{table.columns} cols • {table.rows.toLocaleString()} rows</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <Button
                              onClick={confirmTableSelection}
                              disabled={selectedTables.length === 0}
                              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                              Continue with {selectedTables.length} tables
                            </Button>
                          </div>
                        </StepCard>
                      );
                    }
                    
                    if (message.type === 'table-selection') {
                      return (
                        <StepCard
                          key={message.id}
                          title="Table Selection"
                          status={currentStep === 'table-selection' ? 'active' : (selectedTables.length > 0 ? 'completed' : 'pending')}
                          defaultExpanded={true}
                          autoCollapseOnComplete={true}
                          summary={`${selectedTables.length} tables selected`}
                          duration="2.8s"
                          statusText="Selecting tables"
                        >
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <p className="text-sm text-gray-400">Select tables to include:</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleSelectAllTables}
                                className="text-gray-300 hover:bg-slate-700"
                              >
                                Select All
                              </Button>
                            </div>
                            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                              {message.tables.map((table) => {
                                const isSelected = selectedTables.some(t => t.id === table.id);
                                return (
                                  <div
                                    key={table.id}
                                    onClick={() => handleTableSelection(table)}
                                    className={`p-2.5 rounded-lg cursor-pointer transition-all duration-200 ${
                                      isSelected
                                        ? 'bg-blue-500/15'
                                        : 'bg-slate-900/40 hover:bg-slate-900/60'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-7 h-7 rounded flex items-center justify-center ${
                                        isSelected ? 'bg-blue-500/20' : 'bg-slate-700/50'
                                      }`}>
                                        <Table className={`w-3.5 h-3.5 ${isSelected ? 'text-blue-400' : 'text-gray-400'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-gray-300'}`}>{table.name}</p>
                                        <p className="text-xs text-gray-500 truncate">{table.connectionName}</p>
                                      </div>
                                      <p className="text-xs text-gray-500 flex-shrink-0">{table.columns} cols • {table.rows.toLocaleString()} rows</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {selectedTables.length > 0 && message.status !== 'completed' && (
                              <Button
                                onClick={confirmTableSelection}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all duration-200"
                              >
                                Continue with {selectedTables.length} tables
                              </Button>
                            )}
                          </div>
                        </StepCard>
                      );
                    }
                    
                    if (message.type === 'domain-manual-entry') {
                      const isCompleted = message.status === 'completed';
                      const isClassifying = currentStep === 'classifying';
                      
                      return (
                        <StepCard
                          key={message.id}
                          title="Domain Classification"
                          status={isCompleted ? 'completed' : (isClassifying || isProcessing ? 'active' : 'pending')}
                          defaultExpanded={!isCompleted}
                          autoCollapseOnComplete={true}
                          summary={`${manualDomains.filter(d => d.name).length} domains, ${manualDomains.reduce((acc, d) => acc + d.tables.length, 0)} tables classified`}
                          duration="5.4s"
                          statusText="Classifying domains"
                        >
                          <div className="space-y-3">
                            {/* Show intro text only when not completed and not classifying */}
                            {!isCompleted && !isClassifying && message.introText && (
                              <p className="text-sm text-gray-400 whitespace-pre-line">{message.introText}</p>
                            )}
                            
                            {/* Always show the domain cards with names and tables */}
                            {manualDomains.map((domain, index) => (
                              <div key={domain.id} className="bg-slate-900/40 rounded-lg p-2">
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Left: Domain Name Input */}
                                  <div className="space-y-[3px]">
                                    <Label className="text-gray-400 text-xs">Domain {index + 1}</Label>
                                    <Input
                                      value={domain.name}
                                      onChange={(e) => handleManualDomainChange(domain.id, 'name', e.target.value)}
                                      placeholder="e.g., Customer, Products, Orders"
                                      className="text-white text-xs"
                                      disabled={isCompleted}
                                    />
                                  </div>
                                  
                                  {/* Right: Multi-Select Dropdown */}
                                  <div className="space-y-[3px]">
                                    <Label className="text-gray-400 text-xs">
                                      Select Sample Tables
                                      {domain.tables.length > 0 && (
                                        <span className="ml-1 text-blue-400">({domain.tables.length})</span>
                                      )}
                                    </Label>
                                    <div className="relative">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button 
                                            className="flex h-9 w-full items-center justify-between rounded-md bg-slate-800/60 px-3 py-2 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                                            disabled={isCompleted}
                                          >
                                            <span className="text-gray-600">Choose tables...</span>
                                            <ChevronDown className="h-4 w-4 text-white opacity-50" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent 
                                          className="w-[var(--radix-popover-trigger-width)] p-0 border-slate-700 bg-slate-900"
                                          align="start"
                                        >
                                          <div className="max-h-60 overflow-y-auto">
                                            {selectedTables.map((table) => {
                                              const isSelectedInCurrentDomain = domain.tables.includes(table.id);
                                              
                                              // Check if this table is assigned to another domain
                                              const assignedToDomain = manualDomains.find(d => 
                                                d.id !== domain.id && d.tables.includes(table.id)
                                              );
                                              
                                              return (
                                                <div
                                                  key={table.id}
                                                  onClick={() => !isCompleted && handleDomainTableSelection(domain.id, table.id)}
                                                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-800 ${
                                                    isSelectedInCurrentDomain ? 'bg-slate-800/50' : ''
                                                  }`}
                                                >
                                                  <div className={`w-3.5 h-3.5 rounded flex items-center justify-center ${
                                                    isSelectedInCurrentDomain 
                                                      ? 'bg-blue-600' 
                                                      : 'bg-slate-700'
                                                  }`}>
                                                    {isSelectedInCurrentDomain && (
                                                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                      </svg>
                                                    )}
                                                  </div>
                                                  <span className="text-white text-xs flex-1">{table.name}</span>
                                                  {assignedToDomain && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                                                      {assignedToDomain.name || `Domain ${manualDomains.findIndex(d => d.id === assignedToDomain.id) + 1}`}
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Selected Tables Pills */}
                                {domain.tables.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                                    {domain.tables.slice(0, 3).map((tableId) => {
                                      const table = selectedTables.find(t => t.id === tableId);
                                      if (!table) return null;
                                      return (
                                        <span
                                          key={tableId}
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px]"
                                        >
                                          {table.name}
                                          {!isCompleted && (
                                            <button
                                              onClick={() => handleDomainTableSelection(domain.id, tableId)}
                                              className="hover:text-blue-100 transition-colors"
                                            >
                                              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          )}
                                        </span>
                                      );
                                    })}
                                    {domain.tables.length > 3 && (
                                      <span className="text-gray-500 text-xs">
                                        +{domain.tables.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                            
                            {/* Only show buttons when not completed and not classifying */}
                            {!isCompleted && !isClassifying && (
                              <>
                                <Button
                                  type="button"
                                  onClick={addDomain}
                                  variant="outline"
                                  className="w-full text-gray-300 hover:bg-slate-700"
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add More Domains
                                </Button>
                                
                                <div className="flex gap-3">
                                  <Button
                                    onClick={skipDomainClassification}
                                    variant="outline"
                                    className="flex-1 text-gray-400 hover:bg-slate-700 transition-all duration-200"
                                  >
                                    Skip
                                  </Button>
                                  <Button
                                    onClick={confirmDomainClassification}
                                    disabled={!manualDomains.some(d => d.name && d.tables.length > 0)}
                                    className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                  >
                                    Continue
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </StepCard>
                      );
                    }
                    
                    if (message.type === 'generate-button') {
                      return (
                        <div key={message.id} className="flex justify-center pt-4">
                          <Button
                            onClick={startGeneration}
                            className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700 text-white px-8 py-6 text-lg font-bold rounded-lg shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-blue-400 to-blue-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
                            <Zap className="w-6 h-6 mr-2 relative z-10" />
                            <span className="relative z-10">Generate Semantic Graph</span>
                          </Button>
                        </div>
                      );
                    }
                    
                    // Section Header Message - Subtle separator
                    if (message.type === 'section-header') {
                      const { title } = message.data;
                      
                      return (
                        <div key={message.id} className="flex items-center gap-3 py-2 my-1">
                          <div className="h-px flex-1 bg-slate-700/50"></div>
                          <span className="text-gray-500 text-[10px] font-medium uppercase tracking-wider">{title}</span>
                          <div className="h-px flex-1 bg-slate-700/50"></div>
                        </div>
                      );
                    }
                    
                    // Data Profile Summary Message (combined L1, L2, and Data Quality)
                    if (message.type === 'data-profile-summary') {
                      const { 
                        dimensionCount, measureCount,
                        textCols, intCols, floatCols, dateCols, boolCols, otherCols,
                        avgCompleteness, avgUniqueness, healthRating
                      } = message.data;
                      const isLoading = message.status !== 'completed';
                      
                      return (
                        <StepCard
                          key={message.id}
                          title="Data Profile"
                          status={message.status === 'completed' ? 'completed' : 'active'}
                          defaultExpanded={true}
                          autoCollapseOnComplete={true}
                          summary={`${dimensionCount} dimensions, ${measureCount} measures • Health: ${healthRating}`}
                          duration="8.2s"
                          statusText="Profiling data"
                          stepCount={3}
                        >
                          <div className="space-y-4">
                            {/* L1: Dimensions & Measures */}
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Level 1 • Dimensions & Measures</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                                  <div className="text-2xl font-semibold text-blue-400 h-8 flex items-center justify-center">
                                    {isLoading ? <span className="animate-pulse">--</span> : dimensionCount}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">Dimensions</div>
                                </div>
                                <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                                  <div className="text-2xl font-semibold text-blue-400 h-8 flex items-center justify-center">
                                    {isLoading ? <span className="animate-pulse">--</span> : measureCount}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">Measures</div>
                                </div>
                              </div>
                            </div>
                            
                            {/* L2: Data Types */}
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Level 2 • Data Types</p>
                              <div className="bg-slate-800/60 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                  <tbody className="divide-y divide-slate-700/30">
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Text</td>
                                      <td className="px-3 py-2 text-right text-blue-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : textCols}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Integer</td>
                                      <td className="px-3 py-2 text-right text-blue-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : intCols}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Float</td>
                                      <td className="px-3 py-2 text-right text-blue-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : floatCols}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Date & Time</td>
                                      <td className="px-3 py-2 text-right text-blue-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : dateCols}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Boolean</td>
                                      <td className="px-3 py-2 text-right text-blue-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : boolCols}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="px-3 py-2 text-gray-300">Others</td>
                                      <td className="px-3 py-2 text-right text-gray-400 font-medium">
                                        {isLoading ? <span className="animate-pulse">--</span> : otherCols}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            {/* Data Quality */}
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Data Quality</p>
                              <div className="bg-slate-800/60 rounded-lg p-3 space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-300 text-sm">Completeness</span>
                                  <div className="flex items-center gap-3">
                                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                                      <div className={`h-full bg-blue-500 rounded-full transition-all duration-500`} style={{ width: isLoading ? '0%' : `${avgCompleteness}%` }}></div>
                                    </div>
                                    <span className="text-blue-400 font-medium text-sm w-12 text-right">{isLoading ? <span className="animate-pulse">--</span> : `${avgCompleteness}%`}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-300 text-sm">Uniqueness</span>
                                  <div className="flex items-center gap-3">
                                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                                      <div className={`h-full bg-blue-500 rounded-full transition-all duration-500`} style={{ width: isLoading ? '0%' : `${avgUniqueness}%` }}></div>
                                    </div>
                                    <span className="text-blue-400 font-medium text-sm w-12 text-right">{isLoading ? <span className="animate-pulse">--</span> : `${avgUniqueness}%`}</span>
                                  </div>
                                </div>
                                <div className="pt-2 border-t border-slate-700/50 flex items-center justify-between">
                                  <span className="text-gray-300 text-sm">Overall Health</span>
                                  <span className="text-emerald-400 font-semibold text-sm">{isLoading ? <span className="animate-pulse">--</span> : healthRating}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </StepCard>
                      );
                    }
                    
                    // Relationship Complete Message - includes PK and FK counts
                    if (message.type === 'relationship-complete') {
                      const { relationshipCount, pkCount } = message.data;
                      const isLoading = message.status !== 'completed';
                      
                      return (
                        <StepCard
                          key={message.id}
                          title="Relationship Identification"
                          status={message.status === 'completed' ? 'completed' : 'active'}
                          defaultExpanded={true}
                          autoCollapseOnComplete={true}
                          summary={`${pkCount || 20} PKs, ${relationshipCount} FK relationships`}
                          duration="12.6s"
                          statusText="Identifying relationships"
                        >
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                              <div className="text-2xl font-semibold text-blue-400 h-8 flex items-center justify-center">
                                {isLoading ? <span className="animate-pulse">--</span> : (pkCount || 20)}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">Primary Keys</div>
                            </div>
                            <div className="bg-slate-800/60 rounded-lg p-3 text-center">
                              <div className="text-2xl font-semibold text-blue-400 h-8 flex items-center justify-center">
                                {isLoading ? <span className="animate-pulse">--</span> : relationshipCount}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">FK Relationships</div>
                            </div>
                          </div>
                        </StepCard>
                      );
                    }
                    
                    // Glossary Summary Message
                    if (message.type === 'glossary-summary') {
                      const { termCount, tableCount, attributeCount } = message.data;
                      const displayTermCount = termCount || attributeCount || 0;
                      const isLoading = message.status !== 'completed';
                      
                      return (
                        <StepCard
                          key={message.id}
                          title="Business Glossary"
                          status={message.status === 'completed' ? 'completed' : 'active'}
                          defaultExpanded={true}
                          autoCollapseOnComplete={true}
                          summary={`${displayTermCount} business terms generated`}
                          duration="15.3s"
                          statusText="Generating glossary"
                        >
                          <div className="bg-slate-800/60 rounded-lg p-4 text-center">
                            <div className="text-3xl font-semibold text-blue-400 h-10 flex items-center justify-center">
                              {isLoading ? <span className="animate-pulse">--</span> : displayTermCount}
                            </div>
                            <div className="text-sm text-gray-400 mt-1">Business Terms</div>
                          </div>
                        </StepCard>
                      );
                    }
                    
                    // Graph Ready Message - shown after glossary is complete
                    if (message.type === 'graph-ready') {
                      return (
                        <div key={message.id} className="flex justify-start my-4">
                          <div className="graph-ready-shimmer relative rounded-xl border border-blue-500/25 px-5 py-4 overflow-hidden">
                            {/* Shimmer overlay */}
                            <div className="shimmer-effect absolute inset-0 pointer-events-none" />
                            <div className="relative flex items-center gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                                {/* Custom network graph icon - 8 nodes with breathing room */}
                                <svg className="w-5 h-5 text-blue-400" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                  {/* Center node - filled */}
                                  <circle cx="16" cy="16" r="2.5" fill="currentColor" />
                                  {/* Top - outline */}
                                  <circle cx="16" cy="4" r="2" />
                                  <line x1="16" y1="6.5" x2="16" y2="13" />
                                  {/* Bottom - outline */}
                                  <circle cx="16" cy="28" r="2" />
                                  <line x1="16" y1="19" x2="16" y2="25.5" />
                                  {/* Left - outline */}
                                  <circle cx="4" cy="16" r="2" />
                                  <line x1="6.5" y1="16" x2="13" y2="16" />
                                  {/* Right - outline */}
                                  <circle cx="28" cy="16" r="2" />
                                  <line x1="19" y1="16" x2="25.5" y2="16" />
                                  {/* Top-left - filled */}
                                  <circle cx="7" cy="7" r="2" fill="currentColor" />
                                  <line x1="8.5" y1="8.5" x2="14" y2="14" />
                                  {/* Top-right - filled */}
                                  <circle cx="25" cy="7" r="2" fill="currentColor" />
                                  <line x1="23.5" y1="8.5" x2="18" y2="14" />
                                  {/* Bottom-left - filled */}
                                  <circle cx="7" cy="25" r="2" fill="currentColor" />
                                  <line x1="8.5" y1="23.5" x2="14" y2="18" />
                                  {/* Bottom-right - filled */}
                                  <circle cx="25" cy="25" r="2" fill="currentColor" />
                                  <line x1="23.5" y1="23.5" x2="18" y2="18" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-sm font-medium text-blue-400">
                                  Semantic Data Graph Generated!
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  You can now start exploring and analyzing your data
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Query Response Message - Multi-step data analysis
                    if (message.type === 'query-response') {
                      const { userQuestion, timestamp } = message.data;
                      return (
                        <div key={message.id} className="w-full">
                          <QueryResponseUI
                            userQuestion={userQuestion}
                            timestamp={timestamp}
                            isStreaming={false}
                            onComplete={() => setIsProcessing(false)}
                            onPublish={() => {
                              console.log('Dashboard: onPublish callback triggered, showing publish input form');
                              setPublishState('input');
                            }}
                          />
                        </div>
                      );
                    }
                    
                    // Data Product Builder Message
                    if (message.type === 'data-product-builder') {
                      const { columns } = message.data;
                      
                      // Get all columns including available ones
                      const allColumns = [...getCustomer360Columns(), ...getAvailableColumns()];
                      
                      // Get columns that are currently selected (includes dynamically added ones)
                      const displayColumns = allColumns.filter(col => 
                        columns.some(c => c.id === col.id) || dataProductColumns.includes(col.id)
                      );
                      
                      // Separate dimensions and measures based on effective type (considering user changes)
                      const dimensions = displayColumns.filter(col => {
                        const config = attributeConfigs[col.id] || {};
                        return (config.type || col.type) === 'dimension';
                      });
                      const measures = displayColumns.filter(col => {
                        const config = attributeConfigs[col.id] || {};
                        return (config.type || col.type) === 'measure';
                      });
                      
                      // Render attribute item (shared between dimensions and measures)
                      const renderAttributeItem = (col, isDimension) => {
                        const isSelected = dataProductColumns.includes(col.id);
                        const isPopupOpen = activeConfigPopup === col.id;
                        const config = attributeConfigs[col.id] || {};
                        const aggregation = config.aggregation || 'COUNT';
                        const alias = config.alias || col.name;
                        const isEditing = editingAttributeId === col.id;
                        
                        return (
                          <div
                            key={col.id}
                            onClick={() => {
                              if (!isEditing) {
                                handleDataProductColumnToggle(col.id);
                              }
                            }}
                            className={`p-2.5 rounded-lg transition-all duration-200 relative cursor-pointer group ${
                              isSelected
                                ? 'bg-blue-600/20'
                                : 'bg-slate-900/40 hover:bg-slate-800/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* Type Icon - Clickable */}
                              <button
                                data-popup-trigger="config"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isPopupOpen) {
                                    // Closing - clear temp config
                                    setActiveConfigPopup(null);
                                    setTempConfigPopup(null);
                                  } else {
                                    // Opening - reset temp config to null so it uses actual config
                                    setTempConfigPopup(null);
                                    setActiveConfigPopup(col.id);
                                  }
                                }}
                                className={`w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0 focus:outline-none ${
                                  isDimension ? 'bg-purple-500/20 hover:bg-purple-500/30' : 'bg-green-500/20 hover:bg-green-500/30'
                                }`}
                                title={isDimension ? "Click to change type" : "Click to change type or aggregation"}
                              >
                                <span className={`font-bold ${
                                  isDimension 
                                    ? `text-xs ${isSelected ? 'text-purple-400' : 'text-gray-500'}`
                                    : `text-[9px] ${isSelected ? 'text-green-400' : 'text-gray-500'}`
                                }`}>
                                  {isDimension ? 'Aa' : '123'}
                                </span>
                              </button>
                              
                              {/* Column Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      defaultValue={alias}
                                      autoFocus
                                      onClick={(e) => e.stopPropagation()}
                                      onBlur={(e) => {
                                        const newAlias = e.target.value.trim();
                                        if (newAlias && newAlias !== col.name) {
                                          updateAttributeConfig(col.id, 'alias', newAlias);
                                        } else if (!newAlias || newAlias === col.name) {
                                          // Remove alias if empty or same as original
                                          setAttributeConfigs(prev => {
                                            const updated = { ...prev };
                                            if (updated[col.id]) {
                                              delete updated[col.id].alias;
                                            }
                                            return updated;
                                          });
                                        }
                                        setEditingAttributeId(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.target.blur();
                                        } else if (e.key === 'Escape') {
                                          setEditingAttributeId(null);
                                        }
                                      }}
                                      className="bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:border-blue-500 w-full"
                                    />
                                  ) : (
                                    <>
                                      <span className={`font-medium text-sm truncate ${isSelected ? 'text-white' : 'text-gray-500'}`}>
                                        {isDimension ? alias : `${aggregation} (${alias})`}
                                      </span>
                                      {/* Edit Icon - Visible on hover */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingAttributeId(col.id);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-slate-700 rounded"
                                        title="Rename"
                                      >
                                        <Pencil className="w-3 h-3 text-gray-500 hover:text-gray-300" />
                                      </button>
                                      {/* Filter Icon - Only for dimensions, visible on hover */}
                                      {isDimension && (
                                        <button
                                          data-popup-trigger="filter"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setFilterPopupId(filterPopupId === col.id ? null : col.id);
                                            setFilterSearchQuery('');
                                          }}
                                          className={`transition-opacity p-0.5 hover:bg-slate-700 rounded ${
                                            filterPopupId === col.id || (config.filters && config.filters.length > 0)
                                              ? 'opacity-100' 
                                              : 'opacity-0 group-hover:opacity-100'
                                          }`}
                                          title="Filter values"
                                        >
                                          <Filter className={`w-3 h-3 ${
                                            config.filters && config.filters.length > 0 
                                              ? 'text-blue-400' 
                                              : 'text-gray-500 hover:text-gray-300'
                                          }`} />
                                        </button>
                                      )}
                                      {/* Filter count badge */}
                                      {isDimension && config.filters && config.filters.length > 0 && (
                                        <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">
                                          {config.filters.length}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div className={`flex items-center gap-1 text-xs mt-0.5 ${isSelected ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <span className={isSelected ? 'text-gray-500' : 'text-gray-600'}>{col.name}</span>
                                  <span>|</span>
                                  <span>{col.table}</span>
                                </div>
                                
                                {/* Filter Popover for Dimensions */}
                                {isDimension && filterPopupId === col.id && (
                                  <div 
                                    data-popup="filter"
                                    className="absolute left-0 top-full mt-1 z-20 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-2.5 w-64"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Search */}
                                    <div className="flex items-center gap-2 mb-2 bg-slate-900 rounded px-2 py-1 border border-slate-700">
                                      <Search className="w-3 h-3 text-gray-500" />
                                      <input
                                        type="text"
                                        value={filterSearchQuery}
                                        onChange={(e) => setFilterSearchQuery(e.target.value)}
                                        placeholder="Search values..."
                                        className="flex-1 bg-transparent text-xs text-white focus:outline-none placeholder-gray-500"
                                        autoFocus
                                      />
                                    </div>
                                    
                                    {/* Select All */}
                                    <div className="mb-1.5">
                                      <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-700/50 rounded px-1 py-0.5">
                                        <input
                                          type="checkbox"
                                          checked={config.filters === undefined}
                                          onChange={() => {
                                            setAttributeConfigs(prev => {
                                              const updated = { ...prev };
                                              if (config.filters === undefined) {
                                                // Currently all selected, deselect all
                                                updated[col.id] = {
                                                  ...updated[col.id],
                                                  filters: []
                                                };
                                              } else {
                                                // Currently some or none selected, select all
                                                if (updated[col.id]) {
                                                  delete updated[col.id].filters;
                                                }
                                              }
                                              return updated;
                                            });
                                          }}
                                          className="w-3 h-3 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-0 focus:ring-offset-0"
                                        />
                                        <span className="text-xs text-gray-300">Select All</span>
                                      </label>
                                    </div>
                                    
                                    {/* Values List */}
                                    <div className="max-h-36 overflow-y-auto space-y-0">
                                      {getDimensionValues(col.id, col.name)
                                        .filter(val => val.toLowerCase().includes(filterSearchQuery.toLowerCase()))
                                        .map((value, idx) => {
                                          // If filters undefined = all selected; if filters array exists, check if value is in it
                                          const isChecked = config.filters === undefined || config.filters.includes(value);
                                          return (
                                            <label 
                                              key={idx} 
                                              className="flex items-center gap-2 cursor-pointer hover:bg-slate-700/50 rounded px-1 py-0.5"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => {
                                                  const allValues = getDimensionValues(col.id, col.name);
                                                  setAttributeConfigs(prev => {
                                                    const updated = { ...prev };
                                                    
                                                    if (config.filters === undefined) {
                                                      // Currently in Select All mode, uncheck this value
                                                      updated[col.id] = {
                                                        ...updated[col.id],
                                                        filters: allValues.filter(v => v !== value)
                                                      };
                                                    } else {
                                                      // Already in individual mode
                                                      if (isChecked) {
                                                        // Remove from filters
                                                        updated[col.id] = {
                                                          ...updated[col.id],
                                                          filters: config.filters.filter(f => f !== value)
                                                        };
                                                      } else {
                                                        // Add to filters
                                                        const newFilters = [...config.filters, value];
                                                        // If all values are now selected, switch back to Select All mode
                                                        if (newFilters.length === allValues.length) {
                                                          delete updated[col.id].filters;
                                                        } else {
                                                          updated[col.id] = {
                                                            ...updated[col.id],
                                                            filters: newFilters
                                                          };
                                                        }
                                                      }
                                                    }
                                                    return updated;
                                                  });
                                                }}
                                                className="w-3 h-3 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-0 focus:ring-offset-0"
                                              />
                                              <span className="text-xs text-gray-300 truncate">{value}</span>
                                            </label>
                                          );
                                        })}
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-700">
                                      <button
                                        onClick={() => {
                                          setAttributeConfigs(prev => {
                                            const updated = { ...prev };
                                            if (updated[col.id]) {
                                              delete updated[col.id].filters;
                                            }
                                            return updated;
                                          });
                                        }}
                                        className="text-[10px] text-gray-400 hover:text-white"
                                      >
                                        Clear all
                                      </button>
                                      <button
                                        onClick={() => setFilterPopupId(null)}
                                        className="text-[10px] bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                                      >
                                        Done
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Row Count & Uniqueness */}
                              <div className="flex items-center gap-3 text-[10px]">
                                <div className={`font-medium ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                                  {formatCount(col.rowCount)}
                                </div>
                                <div className={`font-medium ${!isSelected ? 'text-gray-500' : col.uniqueness >= 80 ? 'text-green-400' : col.uniqueness >= 50 ? 'text-yellow-400' : 'text-gray-400'}`}>
                                  {col.uniqueness}%
                                </div>
                              </div>
                            </div>
                            
                            {/* Type/Aggregation Change Popup - Unified */}
                            {isPopupOpen && (() => {
                              // Use temp config if available, otherwise use actual config
                              const popupConfig = tempConfigPopup || { 
                                type: config.type || col.type, 
                                aggregation: config.aggregation || (col.type === 'measure' ? 'COUNT' : null)
                              };
                              const currentType = popupConfig.type;
                              const currentAgg = popupConfig.aggregation || 'COUNT';
                              
                              return (
                                <div data-popup="config" className="absolute left-8 top-full mt-1 z-10 bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-2 min-w-[170px]">
                                  <div className="text-[10px] text-gray-400 mb-1 px-1">Data Type</div>
                                  <div className="space-y-px">
                                    {/* Dimension Option */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setTempConfigPopup({ type: 'dimension', aggregation: null });
                                      }}
                                      className={`w-full flex items-center gap-2 px-1.5 py-1 rounded text-left ${
                                        currentType === 'dimension' ? 'bg-slate-700/50' : 'hover:bg-slate-700/50'
                                      }`}
                                    >
                                      <div className={`w-2.5 h-2.5 rounded-full border-[1.5px] flex items-center justify-center ${
                                        currentType === 'dimension' ? 'border-gray-400' : 'border-slate-500'
                                      }`}>
                                        {currentType === 'dimension' && (
                                          <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                        )}
                                      </div>
                                      <span className={`text-xs ${currentType === 'dimension' ? 'text-white' : 'text-gray-400'}`}>Dimension</span>
                                    </button>
                                    {/* Measure Option */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setTempConfigPopup({ type: 'measure', aggregation: tempConfigPopup?.aggregation || config.aggregation || 'COUNT' });
                                      }}
                                      className={`w-full flex items-center gap-2 px-1.5 py-1 rounded text-left ${
                                        currentType === 'measure' ? 'bg-slate-700/50' : 'hover:bg-slate-700/50'
                                      }`}
                                    >
                                      <div className={`w-2.5 h-2.5 rounded-full border-[1.5px] flex items-center justify-center ${
                                        currentType === 'measure' ? 'border-gray-400' : 'border-slate-500'
                                      }`}>
                                        {currentType === 'measure' && (
                                          <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                        )}
                                      </div>
                                      <span className={`text-xs ${currentType === 'measure' ? 'text-white' : 'text-gray-400'}`}>Measure</span>
                                    </button>
                                  </div>
                                  
                                  {/* Aggregation Options */}
                                  <div className={`border-t border-slate-600 mt-1 pt-1 ${currentType === 'dimension' ? 'opacity-40' : ''}`}>
                                    <div className="text-[10px] text-gray-400 mb-1 px-1">Aggregation</div>
                                    <div className="space-y-px">
                                      {['COUNT', 'COUNTD', 'SUM', 'AVG'].map(agg => (
                                        <button
                                          key={agg}
                                          disabled={currentType === 'dimension'}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (currentType === 'measure') {
                                              setTempConfigPopup({ type: 'measure', aggregation: agg });
                                            }
                                          }}
                                          className={`w-full flex items-center gap-2 px-1.5 py-0.5 rounded text-left ${
                                            currentType === 'dimension' ? 'cursor-not-allowed' : 'hover:bg-slate-700/50'
                                          }`}
                                        >
                                          <div className={`w-2.5 h-2.5 rounded-full border-[1.5px] flex items-center justify-center ${
                                            currentType === 'measure' && currentAgg === agg ? 'border-gray-400' : 'border-slate-500'
                                          }`}>
                                            {currentType === 'measure' && currentAgg === agg && (
                                              <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                            )}
                                          </div>
                                          <span className={`text-xs ${
                                            currentType === 'dimension' 
                                              ? 'text-gray-500' 
                                              : currentAgg === agg ? 'text-white' : 'text-gray-400'
                                          }`}>{agg}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {/* Apply Button */}
                                  <div className="border-t border-slate-600 mt-1 pt-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        // Apply temp config to actual config
                                        if (tempConfigPopup) {
                                          updateAttributeConfig(col.id, 'type', tempConfigPopup.type);
                                          updateAttributeConfig(col.id, 'aggregation', tempConfigPopup.aggregation);
                                          // Switch to appropriate tab
                                          setAttributeTab(tempConfigPopup.type === 'dimension' ? 'dimensions' : 'measures');
                                        }
                                        setTempConfigPopup(null);
                                        setActiveConfigPopup(null);
                                      }}
                                      className="w-full text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 rounded"
                                    >
                                      Apply
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      };
                      
                      return (
                        <div key={message.id} className="bg-slate-800/40 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">📊</span>
                              <h3 className="text-white font-medium">Select Dimensions & Measures</h3>
                            </div>
                            <button
                              onClick={openAttributeSearch}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              + Add
                            </button>
                          </div>
                          
                          {/* Tab Navigation */}
                          <div className="flex items-center gap-1 mb-3 bg-slate-900/60 p-1 rounded-lg">
                            <button
                              onClick={() => setAttributeTab('dimensions')}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 border ${
                                attributeTab === 'dimensions'
                                  ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                                  : 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/50 border-transparent'
                              }`}
                            >
                              <span>Dimensions</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                attributeTab === 'dimensions' ? 'bg-purple-500/30 text-purple-300' : 'bg-slate-700 text-gray-500'
                              }`}>
                                {dimensions.length}
                              </span>
                            </button>
                            <button
                              onClick={() => setAttributeTab('measures')}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 border ${
                                attributeTab === 'measures'
                                  ? 'bg-green-500/20 text-green-300 border-green-500/30'
                                  : 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/50 border-transparent'
                              }`}
                            >
                              <span>Measures</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                attributeTab === 'measures' ? 'bg-green-500/30 text-green-300' : 'bg-slate-700 text-gray-500'
                              }`}>
                                {measures.length}
                              </span>
                            </button>
                          </div>
                          
                          {/* Column Header */}
                          <div className="flex items-center justify-end gap-3 text-[10px] text-gray-500 pr-1 mb-2">
                            <span># Count</span>
                            <span>Unique</span>
                          </div>
                          
                          {/* Tab Content */}
                          <div className="max-h-[380px] overflow-y-auto">
                            {attributeTab === 'dimensions' ? (
                              <div className="space-y-1.5">
                                {dimensions.length > 0 ? (
                                  dimensions.map((col) => renderAttributeItem(col, true))
                                ) : (
                                  <div className="text-center text-gray-500 text-sm py-6">
                                    No dimensions available
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                {measures.length > 0 ? (
                                  measures.map((col) => renderAttributeItem(col, false))
                                ) : (
                                  <div className="text-center text-gray-500 text-sm py-6">
                                    No measures available
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Search Popup */}
                          {showAttributeSearch && (
                            <div ref={attributeSearchRef} className="mt-4 p-3 bg-slate-900/60 rounded-lg border border-slate-600/50">
                              <div className="flex items-center gap-2 mb-3">
                                <Search className="w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={attributeSearchQuery}
                                  onChange={(e) => setAttributeSearchQuery(e.target.value)}
                                  placeholder="Search attributes..."
                                  className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-gray-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => {
                                    setShowAttributeSearch(false);
                                    setAttributeSearchQuery('');
                                  }}
                                  className="text-gray-500 hover:text-white"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {getFilteredSearchResults().slice(0, 8).map(col => (
                                  <div
                                    key={col.id}
                                    onClick={() => {
                                      addAttributesFromSearch([col.id]);
                                      setAttributeConfigs(prev => ({
                                        ...prev,
                                        [col.id]: {
                                          type: attributeSearchType !== 'all' ? attributeSearchType : col.type,
                                          aggregation: (attributeSearchType === 'measure' || col.type === 'measure') ? 'COUNT' : null
                                        }
                                      }));
                                    }}
                                    className="flex items-center justify-between p-2 rounded hover:bg-slate-800/60 cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-5 h-5 rounded flex items-center justify-center ${col.type === 'dimension' ? 'bg-purple-500/20' : 'bg-green-500/20'}`}>
                                        <span className={`text-[10px] font-bold ${col.type === 'dimension' ? 'text-purple-400' : 'text-green-400'}`}>
                                          {col.type === 'dimension' ? 'Aa' : '123'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-white text-sm">{col.name}</span>
                                        <span className="text-gray-500 text-xs ml-2">{col.table}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px]">
                                      <div className="font-medium text-gray-400">{formatCount(col.rowCount)}</div>
                                      <div className={`font-medium ${col.uniqueness >= 80 ? 'text-green-400' : col.uniqueness >= 50 ? 'text-yellow-400' : 'text-gray-400'}`}>
                                        {col.uniqueness}%
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {getFilteredSearchResults().length === 0 && (
                                  <div className="text-center text-gray-500 text-sm py-3">
                                    No matching attributes found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Selection Summary */}
                          <div className="mt-4 pt-3 border-t border-slate-700/30 flex items-center justify-between">
                            <span className="text-sm text-gray-400">
                              {dataProductColumns.length} of {displayColumns.length} selected
                            </span>
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={openAttributeSearch}
                                variant="outline"
                                size="sm"
                                className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                              >
                                + Add
                              </Button>
                              <Button
                                onClick={() => {
                                  const selectedColumnsData = allColumns.filter(col => 
                                    dataProductColumns.includes(col.id)
                                  );
                                  
                                  const dimensionCount = selectedColumnsData.filter(col => {
                                    const config = attributeConfigs[col.id];
                                    return (config?.type || col.type) === 'dimension';
                                  }).length;
                                  const measureCount = selectedColumnsData.filter(col => {
                                    const config = attributeConfigs[col.id];
                                    return (config?.type || col.type) === 'measure';
                                  }).length;
                                  
                                  const confirmMsg = {
                                    id: messages.length + 1,
                                    role: 'assistant',
                                    content: `✓ ${dimensionCount} dimensions and ${measureCount} measures selected for Customer 360.`,
                                    type: 'text'
                                  };
                                  
                                  // Generate SQL query
                                  const sqlQuery = generateSQLQuery(selectedColumnsData, attributeConfigs);
                                  
                                  const sqlMsgId = messages.length + 2;
                                  const sqlMsg = {
                                    id: sqlMsgId,
                                    role: 'assistant',
                                    type: 'sql-query',
                                    data: {
                                      query: sqlQuery,
                                      dimensionCount,
                                      measureCount,
                                      tableCount: [...new Set(selectedColumnsData.map(c => c.table))].length
                                    }
                                  };
                                  
                                  setMessages(prev => [...prev, confirmMsg, sqlMsg]);
                                  
                                  // Scroll to show full SQL box first, then streaming starts (800ms delay in StreamingSQL)
                                  setTimeout(() => {
                                    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                                  }, 100);
                                }}
                                disabled={dataProductColumns.length === 0}
                                className="text-white font-medium transition-all duration-200 hover:brightness-110 disabled:opacity-50"
                                style={{ backgroundColor: '#2D3748' }}
                                size="sm"
                              >
                                Confirm Selection
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // SQL Query message with expand/collapse and streaming effect
                    if (message.type === 'sql-query') {
                      const isExpanded = expandedSqlMessages[message.id] !== false; // Default to expanded
                      const { query } = message.data;
                      
                      const handleToggleExpand = () => {
                        const newExpanded = !isExpanded;
                        setExpandedSqlMessages(prev => ({
                          ...prev,
                          [message.id]: newExpanded
                        }));
                        // Scroll into view after state update
                        setTimeout(() => {
                          sqlMessageRefs.current[message.id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }, 100);
                      };
                      
                      return (
                        <div 
                          key={message.id} 
                          ref={el => sqlMessageRefs.current[message.id] = el}
                          className="bg-slate-800/40 rounded-lg border border-slate-700/30"
                        >
                          {/* Header */}
                          <div 
                            className={`flex items-center justify-between cursor-pointer ${isExpanded ? 'p-4 pb-3' : 'px-4 py-3'}`}
                            onClick={handleToggleExpand}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-base leading-none">🔧</span>
                              <h3 className="text-white font-medium text-sm leading-none">Generated SQL Query</h3>
                            </div>
                            <button className="text-gray-400 hover:text-white transition-colors flex items-center">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          
                          {/* SQL Content - Collapsible */}
                          {isExpanded && (
                            <div className="px-4 pb-4">
                              <div className="bg-slate-900/60 rounded-lg p-3 h-80 overflow-y-auto border border-slate-700/30">
                                <StreamingSQL 
                                  sql={query} 
                                  formatFn={formatSQL} 
                                  messageId={message.id}
                                  hasStreamed={streamedSqlMessages[message.id]}
                                  onStreamComplete={handleStreamComplete}
                                  startDelay={streamedSqlMessages[message.id] ? 0 : 800}
                                />
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="mt-3 pt-3 border-t border-slate-700/30 flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(query);
                                  }}
                                >
                                  Copy
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-400 border-blue-500/50 hover:bg-blue-500/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // TODO: Implement edit functionality
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  className="text-white font-medium transition-all duration-200 hover:brightness-110 disabled:opacity-50"
                                  style={{ backgroundColor: '#2D3748' }}
                                  disabled={isExecutingQuery}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    executeDataProductQuery();
                                  }}
                                >
                                  {isExecutingQuery ? 'Executing...' : 'Execute'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Default text message
                    return (
                      <div key={message.id}>
                        {/* Conversation Separator */}
                        {showSeparator && (
                          <div className="border-t border-slate-800/50 mt-3 mb-6"></div>
                        )}
                        <div
                          className={`group ${message.role === 'user' ? 'flex flex-col items-end' : 'flex justify-start'}`}
                        >
                          <div
                            className={`max-w-[85%] rounded-lg p-3 ${
                              message.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-950/50 text-gray-200'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-line">{message.content}</p>
                          </div>
                          {message.role === 'user' && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-1 mr-1">
                              <button
                                onClick={() => {
                                  setInputMessage(message.content);
                                }}
                                className="p-1 rounded text-gray-500 hover:text-blue-400 transition-colors"
                                title="Edit"
                              >
                                <Pencil className="w-[0.8rem] h-[0.8rem]" />
                              </button>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(message.content);
                                }}
                                className="p-1 rounded text-gray-500 hover:text-green-400 transition-colors"
                                title="Copy"
                              >
                                <Copy className="w-[0.8rem] h-[0.8rem]" />
                              </button>
                              <button
                                onClick={() => {
                                  setMessages(prev => prev.filter(m => m.id !== message.id));
                                }}
                                className="p-1 rounded text-gray-500 hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-[0.8rem] h-[0.8rem]" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Typing Indicator - Shows when processing */}
                  {isProcessing && (
                    <div className="flex justify-start">
                      <div className="flex items-start gap-2 max-w-[85%]">
                        <div 
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1 bg-slate-950/50"
                        >
                          <Bot className="w-4 h-4" style={{ color: currentAgent.color, opacity: 0.7 }} />
                        </div>
                        <div className="bg-slate-950/50 rounded-2xl rounded-tl-sm px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div 
                              className="w-2 h-2 rounded-full animate-bounce"
                              style={{ backgroundColor: currentAgent.color, opacity: 0.7, animationDelay: '0ms' }}
                            />
                            <div 
                              className="w-2 h-2 rounded-full animate-bounce"
                              style={{ backgroundColor: currentAgent.color, opacity: 0.7, animationDelay: '150ms' }}
                            />
                            <div 
                              className="w-2 h-2 rounded-full animate-bounce"
                              style={{ backgroundColor: currentAgent.color, opacity: 0.7, animationDelay: '300ms' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Publish Flow - Separate Section at Bottom */}
                  {publishState === 'input' && (
                    <div className="w-full px-1">
                      <PublishInputForm
                        tableName="Unified Customer 360 View"
                        query={`SELECT source_system, customer_id, customer_name, email, phone
FROM unified_customer_view
WHERE data_quality_score > 0.8
LIMIT 1000`}
                        onCancel={() => setPublishState('idle')}
                        onSubmit={(config) => {
                          setPublishConfig(config);
                          setPublishState('progress');
                        }}
                      />
                    </div>
                  )}
                  
                  {(publishState === 'progress' || publishState === 'complete') && (
                    <div className="w-full px-1 !mt-1">
                      <PublishProgress
                        tableName={publishConfig?.tableName || 'Unified Customer 360 View'}
                        tags={publishConfig?.tags || []}
                        removeLimits={publishConfig?.removeLimits || false}
                        onComplete={() => {
                          setPublishState('complete');
                          setRightPanelView('publish');
                          setHasTriggeredPublish(true);
                        }}
                        onPreviewClick={() => {
                          setRightPanelView('publish');
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Invisible element for auto-scroll */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Chat Input - Emergent Style */}
                <div className="p-3 pt-2">
                  {/* Outer Box - Status container with agent color */}
                  <div 
                    className="rounded-xl overflow-hidden"
                    style={{ 
                      background: `linear-gradient(135deg, ${currentAgent.color}20 0%, ${currentAgent.color}08 60%, rgba(15, 23, 42, 0.95) 100%)`
                    }}
                  >
                    {/* Agent Status Header */}
                    <div className="flex items-center gap-2 pl-3 pr-2 py-2.5">
                      {/* Glowing Indicator with Multi-layer Ripple */}
                      <div className="relative flex items-center justify-center w-6 h-6">
                        {/* Ripple layers when running */}
                        {getAgentStatus() === 'running' && (
                          <>
                            <div 
                              className="absolute w-3.5 h-3.5 rounded-full animate-ping"
                              style={{ 
                                backgroundColor: currentAgent.color,
                                opacity: 0.4,
                                animationDuration: '2.5s'
                              }}
                            />
                            <div 
                              className="absolute w-3.5 h-3.5 rounded-full animate-ping"
                              style={{ 
                                backgroundColor: currentAgent.color,
                                opacity: 0.3,
                                animationDuration: '2.5s',
                                animationDelay: '0.8s'
                              }}
                            />
                            <div 
                              className="absolute w-3.5 h-3.5 rounded-full animate-ping"
                              style={{ 
                                backgroundColor: currentAgent.color,
                                opacity: 0.2,
                                animationDuration: '2.5s',
                                animationDelay: '1.6s'
                              }}
                            />
                          </>
                        )}
                        {/* Core dot */}
                        <div 
                          className="w-[9px] h-[9px] rounded-full z-10"
                          style={{ 
                            backgroundColor: currentAgent.color,
                            boxShadow: `0 0 3px ${currentAgent.color}80`
                          }}
                        />
                      </div>
                      {/* Agent Name & Status */}
                      <span 
                        className="text-sm font-medium"
                        style={{ color: currentAgent.color }}
                      >
                        {currentAgent.name.replace(' Generator', '')} is {getAgentStatus()}
                        {getAgentStatus() === 'running' && '...'}
                      </span>
                    </div>
                    
                    {/* Scope Panel - Accordion Mode (Inside Chat Panel) */}
                    {showScopePanel && scopePanelMode === 'accordion' && (
                      <div className="mx-[5px] mb-2 rounded-xl bg-slate-900/95 border border-blue-500/20 overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center gap-3 p-3 border-b border-slate-700/50">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xs font-semibold text-white">Limit Data Scope</h3>
                            <p className="text-[10px] text-gray-500">Agent can only access selected data</p>
                          </div>
                          <button 
                            onClick={() => setShowScopePanel(false)}
                            className="w-6 h-6 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        
                        {/* Tabs */}
                        <div className="flex gap-1 p-2 border-b border-slate-700/50">
                          {['domains', 'tables', 'apps', 'tags'].map((tab) => (
                            <button
                              key={tab}
                              onClick={() => setScopeActiveTab(tab)}
                              className={`flex-1 px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                                scopeActiveTab === tab
                                  ? 'bg-blue-500/15 text-blue-400'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                              }`}
                            >
                              {tab === 'domains' ? 'Domains' : tab === 'tables' ? 'Tables' : tab === 'apps' ? 'Apps' : 'Tags'}
                            </button>
                          ))}
                        </div>
                        
                        {/* Search */}
                        <div className="p-2 border-b border-slate-700/50">
                          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700/30">
                            <Search className="w-3 h-3 text-gray-500" />
                            <input
                              type="text"
                              placeholder={`Search ${scopeActiveTab}...`}
                              value={scopeSearchQuery}
                              onChange={(e) => setScopeSearchQuery(e.target.value)}
                              className="flex-1 bg-transparent text-xs text-white placeholder-gray-500 outline-none"
                            />
                          </div>
                        </div>
                        
                        {/* List */}
                        <div className="max-h-40 overflow-y-auto p-1.5">
                          {scopeActiveTab === 'domains' ? (
                            scopeDomains
                              .filter(d => d.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                              .map((domain) => (
                                <div
                                  key={domain.id}
                                  onClick={() => toggleScopeDomain(domain.id)}
                                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedScopeDomains.includes(domain.id)
                                      ? 'bg-blue-500/10'
                                      : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                    selectedScopeDomains.includes(domain.id)
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'border-gray-600'
                                  }`}>
                                    {selectedScopeDomains.includes(domain.id) && (
                                      <Check className="w-2 h-2 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                      <span 
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: domain.color }}
                                      />
                                      {domain.name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">{domain.tableCount} tables • {domain.columnCount} columns</p>
                                  </div>
                                </div>
                              ))
                          ) : scopeActiveTab === 'tables' ? (
                            scopeTables
                              .filter(t => t.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                          t.domain.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                              .map((table) => (
                                <div
                                  key={table.id}
                                  onClick={() => toggleScopeTable(table.id)}
                                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedScopeTables.includes(table.id)
                                      ? 'bg-blue-500/10'
                                      : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                    selectedScopeTables.includes(table.id)
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'border-gray-600'
                                  }`}>
                                    {selectedScopeTables.includes(table.id) && (
                                      <Check className="w-2 h-2 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                      <span 
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: table.color }}
                                      />
                                      {table.name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">{table.domain} • {table.columnCount} cols</p>
                                  </div>
                                </div>
                              ))
                          ) : scopeActiveTab === 'apps' ? (
                            scopeDataApps
                              .filter(app => app.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                            app.description.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                              .map((app) => (
                                <div
                                  key={app.id}
                                  onClick={() => toggleScopeDataApp(app.id)}
                                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedScopeDataApps.includes(app.id)
                                      ? 'bg-blue-500/10'
                                      : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                    selectedScopeDataApps.includes(app.id)
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'border-gray-600'
                                  }`}>
                                    {selectedScopeDataApps.includes(app.id) && (
                                      <Check className="w-2 h-2 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                      <span 
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: app.color }}
                                      />
                                      {app.name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">{app.description} • {app.tableCount} tables</p>
                                  </div>
                                </div>
                              ))
                          ) : (
                            scopeTags
                              .filter(tag => tag.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                            tag.description.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                              .map((tag) => (
                                <div
                                  key={tag.id}
                                  onClick={() => toggleScopeTag(tag.id)}
                                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedScopeTags.includes(tag.id)
                                      ? 'bg-blue-500/10'
                                      : 'hover:bg-white/5'
                                  }`}
                                >
                                  <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                    selectedScopeTags.includes(tag.id)
                                      ? 'bg-blue-500 border-blue-500'
                                      : 'border-gray-600'
                                  }`}>
                                    {selectedScopeTags.includes(tag.id) && (
                                      <Check className="w-2 h-2 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                      <span 
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: tag.color }}
                                      />
                                      {tag.name}
                                    </p>
                                    <p className="text-[10px] text-gray-500">{tag.description} • {tag.count} items</p>
                                  </div>
                                </div>
                              ))
                          )}
                        </div>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between p-2.5 border-t border-slate-700/50">
                          <button
                            onClick={clearScope}
                            className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors px-2 py-1"
                          >
                            Clear all
                          </button>
                          <button
                            onClick={() => setShowScopePanel(false)}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 text-slate-900 text-[10px] font-semibold rounded-md transition-colors"
                          >
                            Apply Scope
                          </button>
                        </div>
                      </div>
                    )}
                    
                    
                    {/* Inner Input Box - Matches chatbot background, overlayed with margins */}
                    <div className="mx-[5px] mb-[5px] rounded-lg bg-slate-900/80 px-3 pb-3 pt-2">
                      {/* Scope Selection Pills - Inside input box */}
                      {scopeSelectedCount > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {/* Domain pills */}
                          {selectedScopeDomains.map(domainId => {
                            const domain = scopeDomains.find(d => d.id === domainId);
                            return domain ? (
                              <div
                                key={`domain-${domainId}`}
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white hover:bg-white/10 transition-colors"
                              >
                                <span 
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: domain.color }}
                                />
                                <span className="truncate max-w-[80px]">{domain.name}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleScopeDomain(domainId); }}
                                  className="w-3 h-3 rounded-full flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ) : null;
                          })}
                          {/* Table pills */}
                          {selectedScopeTables.map(tableId => {
                            const table = scopeTables.find(t => t.id === tableId);
                            return table ? (
                              <div
                                key={`table-${tableId}`}
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white hover:bg-white/10 transition-colors"
                              >
                                <span 
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: table.color }}
                                />
                                <span className="truncate max-w-[80px]">{table.name}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleScopeTable(tableId); }}
                                  className="w-3 h-3 rounded-full flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ) : null;
                          })}
                          {/* App pills */}
                          {selectedScopeDataApps.map(appId => {
                            const app = scopeDataApps.find(a => a.id === appId);
                            return app ? (
                              <div
                                key={`app-${appId}`}
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white hover:bg-white/10 transition-colors"
                              >
                                <span 
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: app.color }}
                                />
                                <span className="truncate max-w-[80px]">{app.name}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleScopeDataApp(appId); }}
                                  className="w-3 h-3 rounded-full flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ) : null;
                          })}
                          {/* Tag pills */}
                          {selectedScopeTags.map(tagId => {
                            const tag = scopeTags.find(t => t.id === tagId);
                            return tag ? (
                              <div
                                key={`tag-${tagId}`}
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white hover:bg-white/10 transition-colors"
                              >
                                <span 
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: tag.color }}
                                />
                                <span className="truncate max-w-[80px]">{tag.name}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleScopeTag(tagId); }}
                                  className="w-3 h-3 rounded-full flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                                >
                                  <X className="w-2 h-2" />
                                </button>
                              </div>
                            ) : null;
                          })}
                          {/* Clear all button */}
                          <button
                            onClick={clearScope}
                            className="flex items-center px-2 py-0.5 rounded-full text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                      
                      {/* Main Input Area with @mention support */}
                      <div className="relative">
                        {/* Highlight layer - renders behind textarea to show styled @mentions */}
                        <div 
                          className="absolute inset-0 px-1 py-2 text-sm pointer-events-none whitespace-pre-wrap break-words overflow-hidden"
                          style={{ 
                            fontFamily: 'inherit',
                            lineHeight: 'inherit'
                          }}
                          aria-hidden="true"
                        >
                          {inputMessage ? (
                            (() => {
                              // Build regex pattern from selected mention names
                              if (selectedMentions.length === 0) {
                                return <span className="text-white">{inputMessage}</span>;
                              }
                              
                              const mentionNames = selectedMentions.map(m => m.name);
                              const pattern = new RegExp(`(@(?:${mentionNames.join('|')}))(?=\\s|$)`, 'g');
                              const parts = inputMessage.split(pattern);
                              
                              return parts.map((part, index) => {
                                if (part && part.startsWith('@')) {
                                  const name = part.substring(1);
                                  if (mentionNames.includes(name)) {
                                    return <span key={index} className="text-blue-400">{part}</span>;
                                  }
                                }
                                return <span key={index} className="text-white">{part}</span>;
                              });
                            })()
                          ) : (
                            <span className="text-gray-500/60">{getContextualPlaceholder()}</span>
                          )}
                        </div>
                        
                        <Textarea
                          ref={textareaRef}
                          value={inputMessage}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !showMentionPanel) {
                              e.preventDefault();
                              sendMessage();
                            }
                            if (e.key === 'Escape' && showMentionPanel) {
                              setShowMentionPanel(false);
                            }
                          }}
                          placeholder=""
                          className="w-full !bg-transparent resize-none min-h-[60px] px-1 py-2 text-sm focus:ring-0 focus:outline-none relative z-10"
                          style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            outline: 'none', 
                            boxShadow: 'none',
                            caretColor: 'white',
                            color: 'transparent',
                            WebkitTextFillColor: 'transparent'
                          }}
                          rows={2}
                        />
                        
                        {/* @mention floating panel */}
                        {showMentionPanel && (
                          <div 
                            ref={mentionPanelRef}
                            className="fixed w-64 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl overflow-hidden"
                            style={{
                              bottom: `${mentionPanelPosition.bottom}px`,
                              left: `${mentionPanelPosition.left}px`,
                              zIndex: 9999
                            }}
                          >
                            {/* Header */}
                            <div className="flex items-center gap-2 p-2.5 border-b border-slate-700/50">
                              <span className="text-blue-400 text-sm">@</span>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-white">Reference data</p>
                                <p className="text-[10px] text-gray-500">Select to add context</p>
                              </div>
                            </div>
                            
                            {/* Tabs */}
                            <div className="flex gap-1 p-2 border-b border-slate-700/50">
                              {['tables', 'apps'].map((tab) => (
                                <button
                                  key={tab}
                                  onClick={() => setMentionActiveTab(tab)}
                                  className={`flex-1 px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                                    mentionActiveTab === tab
                                      ? 'bg-blue-500/15 text-blue-400'
                                      : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                                  }`}
                                >
                                  {tab === 'tables' ? 'Tables' : 'Apps'}
                                </button>
                              ))}
                            </div>
                            
                            {/* Search indicator */}
                            {mentionSearchQuery && (
                              <div className="px-2.5 py-1.5 border-b border-slate-700/50">
                                <span className="text-[10px] text-gray-500">Searching: </span>
                                <span className="text-[10px] text-blue-400">{mentionSearchQuery}</span>
                              </div>
                            )}
                            
                            {/* List */}
                            <div className="max-h-48 overflow-y-auto p-1.5">
                              {filteredMentionItems.length > 0 ? (
                                filteredMentionItems.map((item) => (
                                  <div
                                    key={item.id}
                                    onClick={() => handleMentionSelect(item)}
                                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                        <span 
                                          className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                          style={{ backgroundColor: item.color }}
                                        />
                                        {item.name}
                                      </p>
                                      <p className="text-[10px] text-gray-500">{item.description}</p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="px-2.5 py-4 text-center">
                                  <p className="text-[10px] text-gray-500">No matches found</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Bottom Controls Row */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-1">
                          {/* File Attach Button */}
                          <button
                            onClick={handleFileAttach}
                            className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                          
                          {/* Agent Selector - Pill style */}
                          <div className="relative" ref={agentDropdownRef}>
                            <button
                              ref={agentButtonRef}
                              onClick={() => {
                                setShowAgentDropdown(!showAgentDropdown);
                                setShowModelDropdown(false);
                              }}
                              className="flex items-center gap-1.5 h-7 px-2.5 rounded-full hover:bg-white/10 transition-colors text-xs text-gray-300 hover:text-white"
                            >
                              <span className="text-[11px]">{currentAgent.icon}</span>
                              <span>{currentAgent.name.replace(' Generator', '').replace(' Configurator', '')}</span>
                              <ChevronUp className="w-3 h-3 text-gray-500" />
                            </button>
                            
                            {/* Agent Dropdown */}
                            {showAgentDropdown && (
                              <div 
                                className="fixed w-56 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl overflow-hidden"
                                style={{
                                  bottom: `${agentDropdownPosition.bottom}px`,
                                  left: `${agentDropdownPosition.left}px`,
                                  zIndex: 9999
                                }}
                              >
                                <div className="p-1.5">
                                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Apps</p>
                                  {agents.dataApps.map((agent) => (
                                    <button
                                      key={agent.id}
                                      onClick={() => handleAgentSwitch(agent)}
                                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                        currentAgent.id === agent.id ? 'bg-slate-800' : ''
                                      }`}
                                    >
                                      <span className="text-sm">{agent.icon}</span>
                                      <span className="text-xs font-medium text-white">{agent.name}</span>
                                    </button>
                                  ))}
                                </div>
                                
                                <div className="border-t border-slate-700/50 mx-1.5"></div>
                                
                                <div className="p-1.5">
                                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Data Discovery</p>
                                  {agents.dataDiscovery.map((agent) => (
                                    <button
                                      key={agent.id}
                                      onClick={() => handleAgentSwitch(agent)}
                                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                        currentAgent.id === agent.id ? 'bg-slate-800' : ''
                                      }`}
                                    >
                                      <span className="text-sm">{agent.icon}</span>
                                      <span className="text-xs font-medium text-white">{agent.name}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Model Selector - Pill style */}
                          <div className="relative" ref={modelDropdownRef}>
                            <button
                              ref={modelButtonRef}
                              onClick={() => {
                                setShowModelDropdown(!showModelDropdown);
                                setShowAgentDropdown(false);
                              }}
                              className="flex items-center gap-1.5 h-7 px-2.5 rounded-full hover:bg-white/10 transition-colors text-xs text-gray-300 hover:text-white"
                            >
                              <span>{currentModel.name}</span>
                              <ChevronUp className="w-3 h-3 text-gray-500" />
                            </button>
                            
                            {/* Model Dropdown */}
                            {showModelDropdown && (
                              <div 
                                className="fixed w-48 bg-slate-900 border border-slate-700/50 rounded-xl shadow-2xl max-h-80 overflow-visible"
                                style={{
                                  bottom: `${modelDropdownPosition.bottom}px`,
                                  left: `${modelDropdownPosition.left}px`,
                                  zIndex: 9999
                                }}
                              >
                                <div className="p-1.5">
                                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-2.5 py-1.5">Select Model</p>
                                  {llmModels.map((model) => (
                                    <button
                                      key={model.id}
                                      onClick={() => handleModelSwitch(model)}
                                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-slate-800 transition-colors ${
                                        currentModel.id === model.id ? 'bg-slate-800' : ''
                                      }`}
                                    >
                                      <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                                      <div className="text-left">
                                        <p className="text-xs font-medium text-white">{model.name}</p>
                                        <p className="text-[10px] text-gray-500">{model.provider}</p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Scope Limiting Pill */}
                          <div className="relative" ref={scopePanelRef}>
                            <button
                              ref={scopeButtonRef}
                              data-scope-trigger
                              data-testid="scope-trigger-btn"
                              onClick={() => {
                                setShowScopePanel(!showScopePanel);
                                setShowAgentDropdown(false);
                                setShowModelDropdown(false);
                              }}
                              className={`flex items-center gap-1.5 h-7 px-2.5 rounded-full transition-colors text-xs ${
                                showScopePanel || scopeSelectedCount > 0
                                  ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                                  : 'hover:bg-white/10 text-gray-300 hover:text-white'
                              }`}
                            >
                              <Shield className="w-3 h-3" />
                              <span>Scope{scopeSelectedCount > 0 && ` (${scopeSelectedCount})`}</span>
                              <ChevronUp className={`w-3 h-3 text-gray-500 transition-transform ${showScopePanel ? '' : 'rotate-180'}`} />
                            </button>
                            
                            {/* Scope Panel - Floating Mode */}
                            {showScopePanel && scopePanelMode === 'floating' && (
                              <div 
                                className="fixed w-72 bg-slate-900/95 backdrop-blur-sm border border-blue-500/20 rounded-xl shadow-2xl overflow-hidden"
                                style={{ 
                                  bottom: `${scopePanelPosition.bottom}px`,
                                  left: `${scopePanelPosition.left}px`,
                                  zIndex: 9999,
                                  maxHeight: '450px'
                                }}
                              >
                                {/* Header */}
                                <div className="flex items-center gap-3 p-3 border-b border-slate-700/50">
                                  <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                                    <Shield className="w-4 h-4 text-blue-400" />
                                  </div>
                                  <div className="flex-1">
                                    <h3 className="text-xs font-semibold text-white">Limit Data Scope</h3>
                                    <p className="text-[10px] text-gray-500">Agent can only access selected data</p>
                                  </div>
                                  <button 
                                    onClick={() => setShowScopePanel(false)}
                                    className="w-6 h-6 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-500 hover:text-white transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                
                                {/* Tabs */}
                                <div className="flex gap-1 p-2 border-b border-slate-700/50">
                                  {['domains', 'tables', 'apps', 'tags'].map((tab) => (
                                    <button
                                      key={tab}
                                      onClick={() => setScopeActiveTab(tab)}
                                      className={`flex-1 px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                                        scopeActiveTab === tab
                                          ? 'bg-blue-500/15 text-blue-400'
                                          : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                                      }`}
                                    >
                                      {tab === 'domains' ? 'Domains' : tab === 'tables' ? 'Tables' : tab === 'apps' ? 'Apps' : 'Tags'}
                                    </button>
                                  ))}
                                </div>
                                
                                {/* Search */}
                                <div className="p-2 border-b border-slate-700/50">
                                  <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700/30">
                                    <Search className="w-3 h-3 text-gray-500" />
                                    <input
                                      type="text"
                                      placeholder={`Search ${scopeActiveTab}...`}
                                      value={scopeSearchQuery}
                                      onChange={(e) => setScopeSearchQuery(e.target.value)}
                                      className="flex-1 bg-transparent text-xs text-white placeholder-gray-500 outline-none"
                                    />
                                  </div>
                                </div>
                                
                                {/* List */}
                                <div className="max-h-48 overflow-y-auto p-1.5">
                                  {scopeActiveTab === 'domains' ? (
                                    scopeDomains
                                      .filter(d => d.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                                      .map((domain) => (
                                        <div
                                          key={domain.id}
                                          onClick={() => toggleScopeDomain(domain.id)}
                                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                            selectedScopeDomains.includes(domain.id)
                                              ? 'bg-blue-500/10'
                                              : 'hover:bg-white/5'
                                          }`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                            selectedScopeDomains.includes(domain.id)
                                              ? 'bg-blue-500 border-blue-500'
                                              : 'border-gray-600'
                                          }`}>
                                            {selectedScopeDomains.includes(domain.id) && (
                                              <Check className="w-2 h-2 text-white" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                              <span 
                                                className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                                style={{ backgroundColor: domain.color }}
                                              />
                                              {domain.name}
                                            </p>
                                            <p className="text-[10px] text-gray-500">{domain.tableCount} tables • {domain.columnCount} columns</p>
                                          </div>
                                        </div>
                                      ))
                                  ) : scopeActiveTab === 'tables' ? (
                                    scopeTables
                                      .filter(t => t.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                                  t.domain.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                                      .map((table) => (
                                        <div
                                          key={table.id}
                                          onClick={() => toggleScopeTable(table.id)}
                                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                            selectedScopeTables.includes(table.id)
                                              ? 'bg-blue-500/10'
                                              : 'hover:bg-white/5'
                                          }`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                            selectedScopeTables.includes(table.id)
                                              ? 'bg-blue-500 border-blue-500'
                                              : 'border-gray-600'
                                          }`}>
                                            {selectedScopeTables.includes(table.id) && (
                                              <Check className="w-2 h-2 text-white" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                              <span 
                                                className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                                style={{ backgroundColor: table.color }}
                                              />
                                              {table.name}
                                            </p>
                                            <p className="text-[10px] text-gray-500">{table.domain} • {table.columnCount} cols</p>
                                          </div>
                                        </div>
                                      ))
                                  ) : scopeActiveTab === 'apps' ? (
                                    scopeDataApps
                                      .filter(app => app.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                                    app.description.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                                      .map((app) => (
                                        <div
                                          key={app.id}
                                          onClick={() => toggleScopeDataApp(app.id)}
                                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                            selectedScopeDataApps.includes(app.id)
                                              ? 'bg-blue-500/10'
                                              : 'hover:bg-white/5'
                                          }`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                            selectedScopeDataApps.includes(app.id)
                                              ? 'bg-blue-500 border-blue-500'
                                              : 'border-gray-600'
                                          }`}>
                                            {selectedScopeDataApps.includes(app.id) && (
                                              <Check className="w-2 h-2 text-white" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                              <span 
                                                className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                                style={{ backgroundColor: app.color }}
                                              />
                                              {app.name}
                                            </p>
                                            <p className="text-[10px] text-gray-500">{app.description} • {app.tableCount} tables</p>
                                          </div>
                                        </div>
                                      ))
                                  ) : (
                                    scopeTags
                                      .filter(tag => tag.name.toLowerCase().includes(scopeSearchQuery.toLowerCase()) || 
                                                    tag.description.toLowerCase().includes(scopeSearchQuery.toLowerCase()))
                                      .map((tag) => (
                                        <div
                                          key={tag.id}
                                          onClick={() => toggleScopeTag(tag.id)}
                                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                            selectedScopeTags.includes(tag.id)
                                              ? 'bg-blue-500/10'
                                              : 'hover:bg-white/5'
                                          }`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                            selectedScopeTags.includes(tag.id)
                                              ? 'bg-blue-500 border-blue-500'
                                              : 'border-gray-600'
                                          }`}>
                                            {selectedScopeTags.includes(tag.id) && (
                                              <Check className="w-2 h-2 text-white" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate flex items-center gap-1.5">
                                              <span 
                                                className="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                                                style={{ backgroundColor: tag.color }}
                                              />
                                              {tag.name}
                                            </p>
                                            <p className="text-[10px] text-gray-500">{tag.description} • {tag.count} items</p>
                                          </div>
                                        </div>
                                      ))
                                  )}
                                </div>
                                
                                {/* Footer */}
                                <div className="flex items-center justify-between p-2.5 border-t border-slate-700/50">
                                  <button
                                    onClick={clearScope}
                                    className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors px-2 py-1"
                                  >
                                    Clear all
                                  </button>
                                  <button
                                    onClick={() => setShowScopePanel(false)}
                                    className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 text-slate-900 text-[10px] font-semibold rounded-md transition-colors"
                                  >
                                    Apply Scope
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Send Button */}
                        <button
                          onClick={sendMessage}
                          disabled={!inputMessage.trim() || isProcessing}
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                            isProcessing 
                              ? 'bg-blue-500/20 text-blue-400 cursor-not-allowed' 
                              : 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed'
                          }`}
                        >
                          {isProcessing ? (
                            <Square className="w-3 h-3 fill-current" />
                          ) : (
                            <ArrowUp className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          )}

          {/* Resizable Divider with Collapse Controls - Only show when no panel is collapsed */}
          {!collapsedPanel && (
          <div
            className="relative cursor-col-resize group z-30"
            onMouseDown={handleMouseDown}
            style={{ 
              width: '16px',
              margin: '0 -8px'
            }}
          >
            {/* Thin divider line with hover effect */}
            <div className="absolute left-1/2 -translate-x-1/2 w-[1px] h-full bg-slate-700/30 group-hover:bg-blue-500/60 transition-colors z-30" />
            
            {/* Collapse Controls Container - Centered vertically */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity z-50">
              {/* Collapse Left Arrow */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  collapseLeft();
                }}
                className="w-6 h-6 rounded-md flex items-center justify-center transition-all bg-blue-900/40 hover:bg-blue-800/60 text-blue-400 hover:text-blue-300 backdrop-blur-sm"
                title="Collapse Chat"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Drag Handle */}
              <div className="w-[6px] h-6 bg-slate-700 group-hover:bg-blue-500 rounded-full transition-colors" />
              
              {/* Collapse Right Arrow */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  collapseRight();
                }}
                className="w-6 h-6 rounded-md flex items-center justify-center transition-all bg-blue-900/40 hover:bg-blue-800/60 text-blue-400 hover:text-blue-300 backdrop-blur-sm"
                title="Collapse Graph"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            {/* Default handle when not hovering */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[6px] h-6 bg-slate-700 group-hover:opacity-0 rounded-full transition-all z-40" />
          </div>
          )}

          {/* Right: Dynamic Content Area */}
          {collapsedPanel !== 'right' && (
          <div 
            className="flex-1 flex flex-col overflow-hidden relative"
            style={{ width: collapsedPanel === 'left' ? '100%' : `${100 - leftWidth}%` }}
          >
            {/* Right Panel Content - Semantic Graph or Publish View */}
            <div className="absolute inset-0 flex flex-col" data-testid="right-panel-content">
              {/* Tab Bar - Browser-style tabs */}
              {hasTriggeredPublish && (
                <div className="flex items-end px-3 pt-2 bg-slate-950 relative z-10 shrink-0" data-testid="publish-tab-bar">
                  {/* Session Tabs */}
                  <div className="flex items-end gap-1">
                    {/* Semantic Graph Tab */}
                    <div className="relative">
                      <button
                        onClick={() => setRightPanelView('graph')}
                        data-testid="tab-graph"
                        className={`flex items-center gap-2 px-3 py-2 rounded-t-lg text-sm transition-all relative z-10 ${
                          rightPanelView === 'graph'
                            ? 'bg-[#0f172a] text-white'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/40'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${rightPanelView === 'graph' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                        <span className="max-w-[120px] truncate">semantic-graph</span>
                      </button>
                      {/* Curved corners for active tab */}
                      {rightPanelView === 'graph' && (
                        <>
                          <div className="absolute bottom-0 -left-2 w-2 h-2 bg-[#0f172a]">
                            <div className="w-full h-full bg-slate-950 rounded-br-lg" />
                          </div>
                          <div className="absolute bottom-0 -right-2 w-2 h-2 bg-[#0f172a]">
                            <div className="w-full h-full bg-slate-950 rounded-bl-lg" />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Publish Tab */}
                    <div className="relative">
                      <button
                        onClick={() => setRightPanelView('publish')}
                        data-testid="tab-publish"
                        className={`flex items-center gap-2 px-3 py-2 rounded-t-lg text-sm transition-all relative z-10 ${
                          rightPanelView === 'publish'
                            ? 'bg-[#0f172a] text-white'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-slate-800/40'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${rightPanelView === 'publish' ? 'bg-blue-400' : 'bg-gray-500'}`} />
                        <span className="max-w-[120px] truncate">data-publish</span>
                        <span 
                          onClick={(e) => {
                            e.stopPropagation();
                            setRightPanelView('graph');
                          }}
                          className="ml-1 text-gray-500 hover:text-gray-300 cursor-pointer"
                        >×</span>
                      </button>
                      {/* Curved corners for active tab */}
                      {rightPanelView === 'publish' && (
                        <>
                          <div className="absolute bottom-0 -left-2 w-2 h-2 bg-[#0f172a]">
                            <div className="w-full h-full bg-slate-950 rounded-br-lg" />
                          </div>
                          <div className="absolute bottom-0 -right-2 w-2 h-2 bg-[#0f172a]">
                            <div className="w-full h-full bg-slate-950 rounded-bl-lg" />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Content Area - Graph or Publish Panel */}
              <div className="flex-1 relative overflow-hidden">
                {rightPanelView === 'publish' ? (
                  <div className="absolute inset-0 overflow-hidden" data-testid="publish-panel-container">
                    <PublishPanel 
                      tableData={null}
                      onPublish={() => {
                        console.log('Publishing data product...');
                      }}
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0">
                    {/* Progress Indicator - Only show on Semantic Graph view */}
                    {!showDataProductView && (
                      <FloatingProgressPill 
                        currentStep={getProgressStep(currentStep)} 
                        isFullyCompleted={currentStep === 'complete' || currentStep === 'completed' || currentStep === 'graph'}
                        stepDetails={{
                          2: selectedTables.length > 0 ? `${selectedTables.length} tables selected` : '',
                          3: selectedTables.length > 0 
                            ? (currentStep === 'classifying' || currentStep === 'domain-classification' || currentStep === 'domain-manual' || currentStep === 'ready-to-generate'
                              ? `0 of ${selectedTables.length} tables classified`
                              : `${selectedTables.length} of ${selectedTables.length} tables classified`)
                            : '',
                          4: selectedTables.length > 0 
                            ? (currentStep === 'generating'
                              ? `0 of ${selectedTables.length} tables profiled`
                              : currentStep === 'classifying' || currentStep === 'domain-classification' || currentStep === 'domain-manual' || currentStep === 'ready-to-generate'
                              ? `0 of ${selectedTables.length} tables profiled`
                              : `${selectedTables.length} of ${selectedTables.length} tables profiled`)
                            : '',
                          5: selectedTables.length > 0 
                            ? (currentStep === 'relationship-identification'
                              ? `0 of ${selectedTables.length} tables analyzed`
                              : (currentStep === 'business-glossary' || currentStep === 'graph' || currentStep === 'completed')
                              ? `${selectedTables.length} of ${selectedTables.length} tables analyzed`
                              : `0 of ${selectedTables.length} tables analyzed`)
                            : '',
                          6: selectedTables.length > 0 
                            ? `${selectedTables.length} of ${selectedTables.length} tables completed`
                            : 'Completed',
                        }}
                      />
                    )}
                    <SemanticCanvas
                      key="canvas-main"
                      initialNodes={semanticNodes}
                      initialLinks={semanticLinks}
                      width={canvasDimensions.width}
                      height={canvasDimensions.height}
                      nodeRightClickRender={SimpleNodeRightClickRender}
                      linkRightClickRender={SimpleLinkRightClickRender}
                      searchDisplayMode={SearchDisplayMode.HIGHLIGHT}
                      searchType={SearchType.NORMAL}
                      showSearchBar={true}
                      showGranularity={true}
                      showDQToggle={true}
                      showAccuracySlider={false}
                      defaultGranularity={2}
                      physicsParams={physicsParams}
                      shouldReheatSimulation={shouldReheatSimulation}
                      highlightedNodeIds={highlightedNodeIds}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
          
          {/* Collapsed Right Panel Restore Strip */}
          {collapsedPanel === 'right' && (
            <div 
              className="h-full w-10 bg-slate-900 flex flex-col items-center justify-center cursor-pointer transition-colors group relative"
              onClick={collapseRight}
              title="Expand Semantic Graph"
            >
              {/* Hover line - same as divider */}
              <div className="absolute left-0 top-0 w-[1px] h-full bg-slate-700/30 group-hover:bg-blue-500/60 transition-colors" />
              <div className="flex flex-col items-center gap-2">
                <ChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-blue-400 transition-colors" />
                <span className="text-[10px] text-gray-500 group-hover:text-blue-400 transition-colors" style={{ writingMode: 'vertical-rl' }}>Graph</span>
              </div>
            </div>
          )}
        </main>

        {/* Overlay when panel is open */}
        {showConnectionsPanel && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 top-[73px]"
            onClick={() => setShowConnectionsPanel(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
